import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Briefcase, Users, TrendingUp, Plus, X, Search, Mail as MailIcon,
  Phone, Building2, Trash2, ChevronRight, DollarSign, Loader2,
  CheckCircle2, Award, LayoutDashboard,
  Sparkles, Bot, Zap, Megaphone, BarChart3, Send, Copy, RefreshCw,
} from "lucide-react";
import { api } from "../api";
import { useWM } from "../store";
import { APPS } from "./index";
import { Marketing } from "./crm/DepartmentViews";
import { AIVoiceAgentView } from "./crm/AIVoiceAgentView";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell,
} from "recharts";

const STAGES = [
  { id: "lead",         label: "Lead",         color: "#94a3b8" },
  { id: "qualified",    label: "Qualified",    color: "#38bdf8" },
  { id: "proposal",     label: "Proposal",     color: "#a78bfa" },
  { id: "negotiation",  label: "Negotiation",  color: "#f0abfc" },
  { id: "won",          label: "Won",          color: "#34d399" },
  { id: "lost",         label: "Lost",         color: "#fb7185" },
];

function fmt(n, ccy = "INR") {
  const v = Number(n || 0);
  try { return new Intl.NumberFormat("en-IN", { style: "currency", currency: ccy, maximumFractionDigits: 0 }).format(v); }
  catch { return `₹${v.toFixed(0)}`; }
}

// wa.me needs the full country code. Most leads are stored as plain 10-digit
// Indian mobile numbers, so assume country code 91 for those; leave anything
// else (already has a country code, or a weird length) as-is.
function normalizeWhatsAppNumber(phone) {
  const digits = (phone || "").replace(/[^0-9]/g, "");
  return digits.length === 10 ? `91${digits}` : digits;
}

/* ============================================================
   CRM  App — left-sidebar layout (Sales / Marketing / Dashboard)
   ============================================================ */
export default function CRM() {
  const [tab, setTab] = useState("leads");             // primary nav key (unified w/ sub-tabs)
  const [contacts, setContacts] = useState([]);
  const [deals, setDeals] = useState([]);
  const [activities, setActivities] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, d, a, s] = await Promise.all([
        api.get("/crm/contacts"),
        api.get("/crm/deals"),
        api.get("/crm/activities"),
        api.get("/crm/stats"),
      ]);
      setContacts(c.data || []);
      setDeals(d.data || []);
      setActivities(a.data || []);
      setStats(s.data || null);
    } catch (e) {
      console.warn("[CRM] load failed", e?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div
      data-testid="crm-app"
      className="h-full w-full flex flex-col md:flex-row overflow-hidden bg-white text-slate-800"
    >
      <CrmSidebar tab={tab} setTab={setTab} stats={stats} loading={loading} onRefresh={load} />
      <div className="flex-1 min-w-0 min-h-0 overflow-y-auto">
        {tab === "dashboard"   && <CrmDashboard />}
        {tab === "leads"       && <LeadsView />}
        {tab === "pipeline"    && <Pipeline deals={deals} contacts={contacts} refresh={load} />}
        {tab === "contacts"    && <Contacts contacts={contacts} refresh={load} />}
        {tab === "team"        && <TeamView />}
        {tab === "products"    && <ProductsView />}
        {tab === "sources"     && <LeadSourcesView />}
        {tab === "proposals"   && <ProposalsView />}
        {tab === "activity"    && <Activity activities={activities} contacts={contacts} deals={deals} refresh={load} />}
        {tab === "copilot"     && <CopilotView />}
        {tab === "automations" && <AutomationsView />}
        {tab === "ads"         && <AdsView />}
        {tab === "ai-voice"    && <AIVoiceAgentView />}
        {tab === "analytics"   && <AnalyticsView />}
        {tab === "marketing"   && <Marketing />}
      </div>
    </div>
  );
}

/* ------------------------- LEFT SIDEBAR ------------------------- */
function CrmSidebar({ tab, setTab, stats, loading, onRefresh }) {
  const items = [
    { section: "", nodes: [
      { k: "dashboard",   label: "Dashboard",    icon: LayoutDashboard },
    ]},
    { section: "Sales", nodes: [
      { k: "leads",       label: "Leads",        icon: Sparkles },
      { k: "pipeline",    label: "Pipeline",     icon: TrendingUp },
      { k: "contacts",    label: "Contacts",     icon: Users },
      { k: "team",        label: "Team",         icon: Users },
      { k: "products",    label: "Products",     icon: Building2 },
      { k: "sources",     label: "Lead Sources", icon: Megaphone },
      { k: "proposals",   label: "Proposals",    icon: Send },
    ]},
    { section: "Intelligence", nodes: [
      { k: "copilot",     label: "AI Co-Pilot",  icon: Bot },
      { k: "automations", label: "Automations",  icon: Zap },
      { k: "analytics",   label: "Reports",      icon: BarChart3 },
    ]},
    { section: "Growth", nodes: [
      { k: "ads",         label: "Ad Campaigns", icon: Megaphone },
      { k: "ai-voice",     label: "AI Voice Agent", icon: Phone },
      { k: "marketing",   label: "Marketing",    icon: Award },
    ]},
  ];

  return (
    <aside className="w-full md:w-60 md:h-full flex md:flex-col shrink-0 border-b md:border-b-0 md:border-r border-slate-200 bg-white">
      {/* Brand */}
      <div className="hidden md:flex items-center gap-2.5 px-4 py-4 border-b border-slate-100">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white"
             style={{ background: "linear-gradient(140deg,#8b5cf6,#38bdf8)" }}>
          <Briefcase size={15} />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.28em] text-indigo-500 font-medium">HEXA</div>
          <div className="text-[13px] font-semibold text-slate-800 -mt-0.5">CRM Suite</div>
        </div>
      </div>

      {/* Nav — horizontal scroll on mobile, vertical on desktop */}
      <nav className="flex md:flex-col overflow-x-auto md:overflow-y-auto md:overflow-x-visible flex-1 gap-1 md:gap-0.5 p-2 md:p-3" style={{ scrollbarWidth: "none" }}>
        {items.map((section) => (
          <div key={section.section || "top"} className="md:mb-3 contents md:block">
            {section.section && (
              <div className="hidden md:block text-[9px] uppercase tracking-[0.28em] text-slate-400 font-medium px-2 mb-1">
                {section.section}
              </div>
            )}
            {section.nodes.map(n => {
              const Icon = n.icon; const active = tab === n.k;
              return (
                <button
                  key={n.k}
                  data-testid={`crm-nav-${n.k}`}
                  onClick={() => setTab(n.k)}
                  className={`w-full whitespace-nowrap flex items-center gap-2 px-2.5 py-1.5 md:py-2 rounded-md text-[12px] md:text-[13px] transition ${active
                    ? "bg-indigo-50 text-indigo-700 font-semibold"
                    : "text-slate-600 hover:bg-slate-50"}`}
                >
                  <Icon size={14} className={active ? "text-indigo-600" : "text-slate-400"} />
                  {n.label}
                </button>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Stats footer (desktop only) */}
      <div className="hidden md:block px-3 py-3 border-t border-slate-100">
        {stats && (
          <div className="space-y-1.5">
            <FooterStat label="Pipeline" value={fmt(stats.pipeline_value)} tint="#0ea5e9" />
            <FooterStat label="Won"      value={fmt(stats.won_value)}     tint="#10b981" />
            <FooterStat label="Deals"    value={stats.deals_total ?? 0}   tint="#a855f7" />
          </div>
        )}
        <button onClick={onRefresh} disabled={loading}
          data-testid="crm-refresh"
          className="w-full mt-2 flex items-center justify-center gap-1 text-[10px] uppercase tracking-widest text-slate-400 hover:text-indigo-600 py-1"
        >
          {loading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={10} />}
          Refresh
        </button>
      </div>
    </aside>
  );
}

function FooterStat({ label, value, tint }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[10px] uppercase tracking-widest text-slate-400">{label}</span>
      <span className="text-[12px] font-semibold" style={{ color: tint }}>{value}</span>
    </div>
  );
}

/* Legacy Header/SalesSubBar/Stat components removed — replaced by CrmSidebar. */


/* --------------------------- PIPELINE --------------------------- */
/* ==============================================================
   LEAD PIPELINE  (was Deal Pipeline) — drag leads across stages
   ============================================================== */
const LEAD_STAGE_MAP = {
  // stage-id → array of statuses this stage aggregates
  lead:        ["new", "lead", "contacted"],
  qualified:   ["qualified"],
  proposal:    ["proposal"],
  negotiation: ["negotiation"],
  won:         ["converted", "won"],
  lost:        ["lost", "unqualified"],
};
// canonical status to assign when a lead is dropped into a stage
const LEAD_STAGE_TARGET_STATUS = {
  lead: "lead", qualified: "qualified", proposal: "proposal",
  negotiation: "negotiation", won: "won", lost: "lost",
};

function Pipeline({ deals: _unused, contacts, refresh: _refreshParent }) {
  const [allLeads, setAllLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/crm/leads"); setAllLeads(r.data || []); }
    catch (e) { console.warn(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const byStage = useMemo(() => {
    const map = {};
    STAGES.forEach(s => { map[s.id] = []; });
    allLeads.forEach(l => {
      const st = (l.status || "new").toLowerCase();
      const stage = STAGES.find(s => LEAD_STAGE_MAP[s.id].includes(st));
      (map[stage ? stage.id : "lead"]).push(l);
    });
    return map;
  }, [allLeads]);

  const moveLead = async (leadId, newStageId) => {
    const targetStatus = LEAD_STAGE_TARGET_STATUS[newStageId];
    if (!targetStatus) return;
    try {
      await api.put(`/crm/leads/${leadId}`, { status: targetStatus });
      load();
    } catch (e) { console.warn(e); }
  };

  return (
    <div className="p-4 sm:p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[18px] font-semibold text-slate-800">Pipeline</h2>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {loading ? "Loading…" : `${allLeads.length} leads across ${STAGES.length} stages · drag cards to change stage`}
          </div>
        </div>
      </div>

      <div className="grid gap-3 overflow-x-auto crm-leads-scroll pb-2"
        style={{ gridTemplateColumns: `repeat(${STAGES.length}, minmax(220px, 1fr))` }}>
        {STAGES.map((stage) => {
          const list = byStage[stage.id] || [];
          const total = list.reduce((s, l) => s + Number(l.score || 0), 0);
          return (
            <div
              key={stage.id}
              data-testid={`crm-pipeline-stage-${stage.id}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { const id = e.dataTransfer.getData("leadId"); if (id) moveLead(id, stage.id); }}
              className="rounded-md p-2.5 min-h-[300px] flex flex-col"
              style={{ background: `${stage.color}0d`, border: `1px solid ${stage.color}44` }}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-[0.22em] font-semibold" style={{ color: stage.color }}>
                  {stage.label}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">
                  {list.length}{total ? ` · Σ${total}` : ""}
                </div>
              </div>
              <div className="flex-1 space-y-1.5 overflow-y-auto">
                {list.map(l => <LeadCard key={l.id} lead={l} tint={stage.color} />)}
                {list.length === 0 && (
                  <div className="text-[10px] uppercase tracking-widest text-slate-300 text-center py-6">
                    drop leads here
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LeadCard({ lead, tint }) {
  const pchip = productChip(lead.product || lead.company || "");
  return (
    <div
      data-testid={`crm-pipeline-lead-${lead.id}`}
      draggable
      onDragStart={(e) => e.dataTransfer.setData("leadId", lead.id)}
      className="group rounded p-2.5 cursor-move transition hover:-translate-y-0.5"
      style={{ background: "#ffffff", border: `1px solid ${tint}55` }}
    >
      <div className="text-[12.5px] font-medium leading-snug truncate text-slate-800">{lead.name}</div>
      {lead.company && (
        <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5 truncate">{lead.company}</div>
      )}
      <div className="flex items-center gap-1 mt-1.5 flex-wrap">
        {lead.product && (
          <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: pchip.bg, color: pchip.fg }}>
            {lead.product}
          </span>
        )}
        {typeof lead.score === "number" && (
          <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold"
            style={{ background: `${scoreTint(lead.score)}22`, color: scoreTint(lead.score) }}>
            {lead.score}
          </span>
        )}
      </div>
      {lead.phone && (
        <div className="text-[10.5px] text-slate-500 mt-1 truncate">{lead.phone}</div>
      )}
    </div>
  );
}

function DealCard({ deal, tint, refresh }) {
  const del = async () => {
    if (!window.confirm(`Delete deal "${deal.title}"?`)) return;
    try { await api.delete(`/crm/deals/${deal.id}`); refresh(); } catch (e) { console.warn(e); }
  };
  return (
    <div
      data-testid={`crm-deal-${deal.id}`}
      draggable
      onDragStart={(e) => e.dataTransfer.setData("dealId", deal.id)}
      className="group rounded p-2.5 cursor-move transition hover:-translate-y-0.5"
      style={{ background: "#ffffff", border: `1px solid ${tint}55` }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-[12.5px] font-medium leading-snug flex-1">{deal.title}</div>
        <button onClick={del} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-400 p-0.5">
          <Trash2 size={11} />
        </button>
      </div>
      {deal.contact && (
        <div className="mono text-[9.5px] uppercase tracking-widest text-slate-500 mt-1 flex items-center gap-1">
          <Building2 size={9} /> {deal.contact.company || deal.contact.name}
        </div>
      )}
      <div className="flex items-center justify-between mt-2">
        <div className="mono text-[11px] font-semibold" style={{ color: tint }}>
          {fmt(deal.value, deal.currency)}
        </div>
        <div className="mono text-[9px] uppercase tracking-widest text-slate-400">
          {deal.probability ?? 20}%
        </div>
      </div>
    </div>
  );
}

/* --------------------------- PROPOSALS (per lead) --------------------------- */
// Lists every proposal created from a lead (the Create Proposal button), by lead number.
function PickLeadForProposal({ onClose, onPick }) {
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/crm/leads").then(r => setLeads(r.data || [])).catch(e => console.warn(e)); }, []);
  const s = q.trim().toLowerCase();
  const shown = (s ? leads.filter(l => [l.lead_number, l.name, l.company].some(v => String(v || "").toLowerCase().includes(s))) : leads).slice(0, 50);
  return (
    <ModalShell title="Create Proposal" icon={Send} onClose={onClose}>
      <div className="text-[12px] text-slate-500 mb-2">Pick the lead this proposal is for. It will be linked to the lead number.</div>
      <ToolbarSearch testId="crm-proposal-pick-search" value={q} onChange={setQ} placeholder="Search lead number or name..." />
      <div className="mt-2 max-h-64 overflow-y-auto rounded-md border border-slate-200 divide-y divide-slate-100">
        {shown.map(l => (
          <button key={l.id} data-testid={`crm-proposal-pick-${l.id}`} onClick={() => onPick(l)}
            className="w-full text-left px-3 py-2 text-[13px] hover:bg-slate-50 flex items-center gap-3">
            <span className="font-mono text-[11px] text-slate-500 w-16 shrink-0">{l.lead_number || "-"}</span>
            <span className="text-slate-800 truncate">{l.name || "(no name)"}{l.company ? <span className="text-slate-400"> · {l.company}</span> : null}</span>
          </button>
        ))}
        {shown.length === 0 && <div className="px-3 py-6 text-center text-[12px] text-slate-500">No leads found.</div>}
      </div>
      <button data-testid="crm-proposal-pick-none" onClick={() => onPick(null)} className="mt-3 text-[12px] text-indigo-600 hover:underline">Start a proposal without a lead</button>
    </ModalShell>
  );
}

function ProposalsView() {
  const { open: openApp } = useWM();
  const [picking, setPicking] = useState(false);
  const startProposal = (lead) => {
    const app = APPS.find(a => a.id === "proposal");
    if (app) openApp(app, lead ? { fromLead: { ...lead } } : undefined);
    setPicking(false);
  };
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, d] = await Promise.all([
        api.get("/crm/leads"),
        api.get("/documents", { params: { kind: "proposal" } }),
      ]);
      const byId = Object.fromEntries((l.data || []).map(x => [x.id, x]));
      setRows((d.data || []).filter(p => p.crm_lead_id && byId[p.crm_lead_id]).map(p => ({ ...p, lead: byId[p.crm_lead_id] })));
    } catch (e) { console.warn(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const q = query.trim().toLowerCase();
  const shown = q ? rows.filter(r => [r.lead.lead_number, r.lead.name, r.lead.company, r.title].some(v => String(v || "").toLowerCase().includes(q))) : rows;

  return (
    <div className="p-4 sm:p-5 space-y-3" data-testid="crm-proposals-view">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[18px] font-semibold text-slate-800">Proposals</div>
          <div className="text-[11px] text-slate-500 mt-0.5">{shown.length} of {rows.length} proposals created for leads</div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="crm-proposals-refresh" onClick={load}
            className="text-[12px] px-3 py-1.5 rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 flex items-center gap-1">
            <RefreshCw size={12} /> Refresh
          </button>
          <NewButton testId="crm-proposals-new" label="Create Proposal" onClick={() => setPicking(true)} />
        </div>
      </div>
      {picking && <PickLeadForProposal onClose={() => setPicking(false)} onPick={startProposal} />}
      <ToolbarSearch testId="crm-proposals-search" value={query} onChange={setQuery} placeholder="Search lead number, name or proposal..." />
      {loading && <div className="text-[11px] text-slate-500">Loading...</div>}
      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-[13px] min-w-[720px]">
          <thead className="text-slate-500 bg-slate-50">
            <tr>
              <th className="px-3 py-2.5 text-left font-medium text-[11px] uppercase tracking-wider">Lead #</th>
              <th className="px-3 py-2.5 text-left font-medium text-[11px] uppercase tracking-wider">Lead</th>
              <th className="px-3 py-2.5 text-left font-medium text-[11px] uppercase tracking-wider">Proposal</th>
              <th className="px-3 py-2.5 text-left font-medium text-[11px] uppercase tracking-wider">Lead stage</th>
              <th className="px-3 py-2.5 text-left font-medium text-[11px] uppercase tracking-wider">Last updated</th>
            </tr>
          </thead>
          <tbody>
            {shown.map(r => (
              <tr key={r.id} data-testid={`crm-proposal-row-${r.id}`} className="border-t border-slate-100">
                <td className="px-3 py-2.5 font-mono text-[11.5px] text-slate-600 whitespace-nowrap">{r.lead.lead_number || "-"}</td>
                <td className="px-3 py-2.5 text-slate-800">{r.lead.name || "(no name)"}{r.lead.company ? <span className="text-slate-400"> · {r.lead.company}</span> : null}</td>
                <td className="px-3 py-2.5 text-slate-700">{r.title || "Proposal"}</td>
                <td className="px-3 py-2.5 text-slate-600">{(STAGES.find(st => st.id === r.lead.status) || {}).label || r.lead.status || "-"}</td>
                <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{r.updated || r.created ? new Date(r.updated || r.created).toLocaleString() : "-"}</td>
              </tr>
            ))}
            {!loading && shown.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-500 text-[12px]">
                No proposals for leads yet. Open a lead and click <b>Create Proposal</b>.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* --------------------------- SHARED TOOLBAR PIECES --------------------------- */
function AskAIButton({ onClick, testId }) {
  return (
    <button data-testid={testId} onClick={onClick}
      className="text-[12px] font-medium px-3 py-1.5 rounded-md flex items-center gap-1.5 text-white transition"
      style={{ background: "linear-gradient(120deg,#8b5cf6,#38bdf8)" }}>
      <Sparkles size={13} /> Ask AI
    </button>
  );
}

function NewButton({ onClick, testId, label }) {
  return (
    <button data-testid={testId} onClick={onClick}
      className="text-[12px] font-medium px-3 py-1.5 rounded-md flex items-center gap-1 text-white"
      style={{ background: "#6366f1" }}>
      <Plus size={13} /> {label}
    </button>
  );
}

function ToolbarSearch({ value, onChange, placeholder, testId }) {
  return (
    <div className="flex justify-end">
      <div className="relative w-full max-w-xs">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          className="w-full pl-8 pr-3 py-1.5 text-[12px] rounded-md border border-slate-200 bg-white outline-none focus:border-indigo-400" />
      </div>
    </div>
  );
}

function AIAskModal({ title, busy, text, onClose }) {
  return (
    <ModalShell title={title} icon={Sparkles} onClose={onClose}>
      {busy ? (
        <div className="flex items-center gap-2 text-[13px] text-slate-600 py-6 justify-center">
          <Loader2 size={16} className="animate-spin text-indigo-600" />
          Analysing your CRM data...
        </div>
      ) : (
        <div data-testid="crm-ai-ask-text" className="text-[13px] text-slate-700 whitespace-pre-wrap leading-relaxed">{text}</div>
      )}
    </ModalShell>
  );
}

function useCrmAskAI(scope) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState("");
  const ask = async () => {
    setOpen(true); setBusy(true); setText("");
    try {
      const r = await api.post("/crm/ai-ask", { scope });
      setText(r.data.recommendation || "");
    } catch (e) {
      setText("AI summary failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };
  return { open, busy, text, ask, close: () => setOpen(false) };
}

// Contacts are leads, so creating a contact opens the lead form with its own title.
function NewContactFromLead({ onClose, onCreated }) {
  const [team, setTeam] = useState([]);
  const [sourcesList, setSourcesList] = useState([]);
  useEffect(() => {
    Promise.all([api.get("/crm/team"), api.get("/crm/sources")])
      .then(([t, src]) => { setTeam(t.data || []); setSourcesList(src.data || []); })
      .catch(e => console.warn(e));
  }, []);
  return <NewLeadModal title="New Contact" team={team} sourcesList={sourcesList} onClose={onClose} onCreated={onCreated} />;
}

/* --------------------------- CONTACTS --------------------------- */
function Contacts({ contacts: _legacyContacts, refresh: _refresh }) {
  // Show all LEADS as contacts with their current status
  const [leads, setLeads] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const ai = useCrmAskAI("contacts");

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/crm/leads"); setLeads(r.data || []); }
    catch (e) { console.warn(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!query.trim()) return leads;
    const q = query.toLowerCase();
    return leads.filter(l =>
      (l.name || "").toLowerCase().includes(q) ||
      (l.email || "").toLowerCase().includes(q) ||
      (l.phone || "").toLowerCase().includes(q) ||
      (l.company || "").toLowerCase().includes(q) ||
      (l.city || "").toLowerCase().includes(q) ||
      (l.product || "").toLowerCase().includes(q)
    );
  }, [leads, query]);

  return (
    <div className="p-4 sm:p-5 space-y-3" data-testid="crm-contacts-view">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[18px] font-semibold text-slate-800">Contacts</div>
          <div className="text-[11px] text-slate-500 mt-0.5">{filtered.length} of {leads.length} leads · with current status</div>
        </div>
        <div className="flex items-center gap-2">
          <AskAIButton testId="crm-contacts-ai" onClick={ai.ask} />
          <NewButton testId="crm-contacts-new" label="New Contact" onClick={() => setCreating(true)} />
        </div>
      </div>
      <ToolbarSearch testId="crm-contacts-search" value={query} onChange={setQuery} placeholder="Search name, email, phone..." />
      {ai.open && <AIAskModal title="AI Contacts Insight" busy={ai.busy} text={ai.text} onClose={ai.close} />}
      {creating && <NewContactFromLead onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}

      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-[13px] min-w-[900px]">
          <thead className="text-slate-500 bg-slate-50">
            <tr>
              <th className="px-3 py-2.5 text-left font-medium text-[11px] uppercase tracking-wider">Name</th>
              <th className="px-3 py-2.5 text-left font-medium text-[11px] uppercase tracking-wider">Phone</th>
              <th className="px-3 py-2.5 text-left font-medium text-[11px] uppercase tracking-wider">Email</th>
              <th className="px-3 py-2.5 text-left font-medium text-[11px] uppercase tracking-wider">City</th>
              <th className="px-3 py-2.5 text-left font-medium text-[11px] uppercase tracking-wider">Product</th>
              <th className="px-3 py-2.5 text-left font-medium text-[11px] uppercase tracking-wider">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((l) => {
              const sChip = STATUS_CHIP[l.status] || STATUS_CHIP.new;
              const pchip = productChip(l.product || "");
              const initial = (l.name || "?")[0].toUpperCase();
              return (
                <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`crm-contact-${l.id}`}>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <span className="w-7 h-7 rounded-full grid place-items-center text-white text-[12px] font-semibold"
                        style={{ background: `linear-gradient(140deg, ${pchip.fg}, #8b5cf6)` }}>{initial}</span>
                      <span className="font-medium text-slate-800">{l.name || "(no name)"}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-slate-600">{l.phone || "—"}</td>
                  <td className="px-3 py-2.5">
                    {l.email
                      ? <a className="text-indigo-600 hover:underline" href={`mailto:${l.email}`}>{l.email}</a>
                      : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-3 py-2.5 text-slate-600">{l.city || "—"}</td>
                  <td className="px-3 py-2.5">
                    {l.product
                      ? <span className="inline-block text-[12px] px-2 py-0.5 rounded-md" style={{ background: pchip.bg, color: pchip.fg }}>{l.product}</span>
                      : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className="inline-block text-[12px] px-2 py-0.5 rounded-md" style={{ background: sChip.bg, color: sChip.fg }}>
                      {sChip.label}
                    </span>
                  </td>
                </tr>
              );
            })}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} className="text-center py-8 text-[12px] text-slate-400">
                No contacts yet — add a lead in <b>Leads</b> and it will show here with its current status.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* --------------------------- ACTIVITY --------------------------- */
function Activity({ activities, contacts, deals, refresh }) {
  const contactsMap = useMemo(() => Object.fromEntries(contacts.map(c => [c.id, c])), [contacts]);
  const dealsMap    = useMemo(() => Object.fromEntries(deals.map(d => [d.id, d])), [deals]);
  const [creating, setCreating] = useState(false);

  const iconFor = (k) => ({ call: Phone, email: MailIcon, note: CheckCircle2, meeting: Briefcase, task: TrendingUp }[k] || CheckCircle2);
  const tintFor = (k) => ({ call: "#38bdf8", email: "#a78bfa", note: "#f0abfc", meeting: "#34d399", task: "#facc15" }[k] || "#94a3b8");

  return (
    <div className="p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Award size={16} className="text-[#f0abfc]" /> Activity Timeline
        </h2>
        <button
          data-testid="crm-new-activity"
          onClick={() => setCreating(true)}
          className="mono text-[10px] uppercase tracking-[0.24em] px-3 py-1.5 rounded flex items-center gap-1.5"
          style={{ background: "linear-gradient(120deg,#8b5cf6,#38bdf8)", color: "#fff" }}
        >
          <Plus size={11} /> Log Activity
        </button>
      </div>

      <div className="space-y-2">
        {activities.map((a) => {
          const Icon = iconFor(a.kind);
          const tint = tintFor(a.kind);
          const c = a.contact_id ? contactsMap[a.contact_id] : null;
          const d = a.deal_id    ? dealsMap[a.deal_id]       : null;
          return (
            <div
              key={a.id}
              data-testid={`crm-activity-${a.id}`}
              className="flex gap-3 p-3 rounded"
              style={{ background: "#ffffff", border: "1px solid rgba(15,23,42,0.10)" }}
            >
              <div className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0"
                   style={{ background: `${tint}22`, border: `1px solid ${tint}55` }}>
                <Icon size={13} style={{ color: tint }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="mono text-[9px] uppercase tracking-[0.3em]" style={{ color: tint }}>{a.kind}</span>
                  {c && <span className="mono text-[9px] uppercase tracking-widest text-slate-500">→ {c.name}</span>}
                  {d && <span className="mono text-[9px] uppercase tracking-widest text-slate-500">· {d.title}</span>}
                  <span className="mono text-[9px] uppercase tracking-widest text-slate-400 ml-auto">
                    {(a.at || "").slice(0, 10)}
                  </span>
                </div>
                <div className="text-[13px] text-slate-700 mt-1">{a.body}</div>
              </div>
            </div>
          );
        })}
        {activities.length === 0 && (
          <div className="mono text-[11px] uppercase tracking-widest text-slate-400 text-center py-8">
            // no activity yet
          </div>
        )}
      </div>

      {creating && <NewActivityModal contacts={contacts} deals={deals} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); refresh(); }} />}
    </div>
  );
}

/* --------------------------- MODALS --------------------------- */
function ModalShell({ title, icon: Icon, onClose, children }) {
  return (
    <div className="fixed inset-0 z-[500] bg-black/60 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-[min(520px,94vw)] rounded-xl border overflow-hidden shadow-2xl"
        style={{ background: "#ffffff", borderColor: "rgba(15,23,42,0.15)" }}
      >
        <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: "rgba(15,23,42,0.08)" }}>
          <Icon size={14} className="text-indigo-600" />
          <div className="mono text-[11px] uppercase tracking-[0.28em] text-slate-800">{title}</div>
          <button onClick={onClose} className="ml-auto text-slate-500 hover:text-slate-800 p-1"><X size={14} /></button>
        </div>
        <div className="p-4 space-y-3">{children}</div>
      </div>
      <style>{`
        .cinput {
          width: 100%;
          background: #ffffff;
          border: 1px solid rgba(15,23,42,0.10);
          padding: 8px 10px; border-radius: 4px; font-size: 13px;
          color: #1e293b; outline: none;
        }
        .cinput:focus { border-color: rgba(139,92,246,0.55); }
      `}</style>
    </div>
  );
}

function ModalField({ label, children }) {
  return (
    <label className="block">
      <div className="mono text-[9px] uppercase tracking-[0.28em] text-slate-500 mb-1">{label}</div>
      {children}
    </label>
  );
}

function ModalActions({ onCancel, onSave, busy, testId, label = "Save" }) {
  return (
    <div className="flex items-center justify-end gap-2 pt-2">
      <button onClick={onCancel} className="mono text-[10px] uppercase tracking-[0.24em] text-slate-500 hover:text-slate-800 px-3 py-1.5">Cancel</button>
      <button data-testid={testId} onClick={onSave} disabled={busy}
              className="mono text-[10px] uppercase tracking-[0.24em] px-4 py-1.5 rounded flex items-center gap-1.5"
              style={{ background: "linear-gradient(120deg,#8b5cf6,#38bdf8)", color: "#fff" }}>
        {busy ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />}
        {label}
      </button>
    </div>
  );
}

function NewContactModal({ onClose, onCreated }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", company: "", title: "", notes: "", tags: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const save = async () => {
    if (!f.name.trim()) { setErr("Name required"); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/crm/contacts", { ...f, tags: f.tags.split(",").map(t => t.trim()).filter(Boolean) });
      onCreated && onCreated();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title="New Contact" icon={Users} onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <ModalField label="Full name"><input data-testid="crm-c-name" className="cinput" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} autoFocus /></ModalField>
        <ModalField label="Title"><input className="cinput" value={f.title} onChange={e => setF({ ...f, title: e.target.value })} /></ModalField>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <ModalField label="Email"><input data-testid="crm-c-email" className="cinput" type="email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} /></ModalField>
        <ModalField label="Phone"><input className="cinput" value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} /></ModalField>
      </div>
      <ModalField label="Company"><input data-testid="crm-c-company" className="cinput" value={f.company} onChange={e => setF({ ...f, company: e.target.value })} /></ModalField>
      <ModalField label="Tags (comma separated)"><input className="cinput" value={f.tags} onChange={e => setF({ ...f, tags: e.target.value })} placeholder="hot-lead, enterprise" /></ModalField>
      <ModalField label="Notes"><textarea className="cinput" rows={3} value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} /></ModalField>
      {err && <div className="text-[11px] text-rose-500 mono">{err}</div>}
      <ModalActions onCancel={onClose} onSave={save} busy={busy} testId="crm-c-save" />
    </ModalShell>
  );
}

function NewDealModal({ contacts, onClose, onCreated }) {
  const [f, setF] = useState({ title: "", contact_id: "", value: 0, currency: "INR", stage: "lead", probability: 20, close_date: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const save = async () => {
    if (!f.title.trim()) { setErr("Title required"); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/crm/deals", { ...f, value: Number(f.value || 0), probability: Number(f.probability || 0) });
      onCreated && onCreated();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title="New Deal" icon={DollarSign} onClose={onClose}>
      <ModalField label="Deal title"><input data-testid="crm-d-title" className="cinput" value={f.title} onChange={e => setF({ ...f, title: e.target.value })} autoFocus placeholder="e.g. Acme Corp — Q1 platform license" /></ModalField>
      <div className="grid grid-cols-2 gap-3">
        <ModalField label="Contact">
          <select className="cinput" value={f.contact_id} onChange={e => setF({ ...f, contact_id: e.target.value })}>
            <option value="">— none —</option>
            {contacts.map(c => <option key={c.id} value={c.id}>{c.name}{c.company ? ` · ${c.company}` : ""}</option>)}
          </select>
        </ModalField>
        <ModalField label="Stage">
          <select data-testid="crm-d-stage" className="cinput" value={f.stage} onChange={e => setF({ ...f, stage: e.target.value })}>
            {STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </ModalField>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <ModalField label="Value"><input data-testid="crm-d-value" className="cinput" type="number" value={f.value} onChange={e => setF({ ...f, value: e.target.value })} /></ModalField>
        <ModalField label="Currency">
          <select className="cinput" value={f.currency} onChange={e => setF({ ...f, currency: e.target.value })}>
            <option>INR</option><option>USD</option><option>EUR</option><option>GBP</option>
          </select>
        </ModalField>
        <ModalField label="Probability %"><input className="cinput" type="number" min={0} max={100} value={f.probability} onChange={e => setF({ ...f, probability: e.target.value })} /></ModalField>
      </div>
      <ModalField label="Close date"><input className="cinput" type="date" value={f.close_date} onChange={e => setF({ ...f, close_date: e.target.value })} /></ModalField>
      <ModalField label="Notes"><textarea className="cinput" rows={3} value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} /></ModalField>
      {err && <div className="text-[11px] text-rose-500 mono">{err}</div>}
      <ModalActions onCancel={onClose} onSave={save} busy={busy} testId="crm-d-save" />
    </ModalShell>
  );
}

function NewActivityModal({ contacts, deals, onClose, onCreated }) {
  const [f, setF] = useState({ kind: "note", body: "", contact_id: "", deal_id: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const save = async () => {
    if (!f.body.trim()) { setErr("Body required"); return; }
    setBusy(true); setErr("");
    try {
      const payload = { kind: f.kind, body: f.body };
      if (f.contact_id) payload.contact_id = f.contact_id;
      if (f.deal_id)    payload.deal_id    = f.deal_id;
      await api.post("/crm/activities", payload);
      onCreated && onCreated();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title="Log Activity" icon={Award} onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <ModalField label="Kind">
          <select data-testid="crm-a-kind" className="cinput" value={f.kind} onChange={e => setF({ ...f, kind: e.target.value })}>
            <option value="call">Call</option>
            <option value="email">Email</option>
            <option value="note">Note</option>
            <option value="meeting">Meeting</option>
            <option value="task">Task</option>
          </select>
        </ModalField>
        <ModalField label="Contact">
          <select className="cinput" value={f.contact_id} onChange={e => setF({ ...f, contact_id: e.target.value })}>
            <option value="">— none —</option>
            {contacts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </ModalField>
      </div>
      <ModalField label="Deal (optional)">
        <select className="cinput" value={f.deal_id} onChange={e => setF({ ...f, deal_id: e.target.value })}>
          <option value="">— none —</option>
          {deals.map(d => <option key={d.id} value={d.id}>{d.title}</option>)}
        </select>
      </ModalField>
      <ModalField label="Body"><textarea data-testid="crm-a-body" className="cinput" rows={5} value={f.body} onChange={e => setF({ ...f, body: e.target.value })} placeholder="What happened? Any next steps?" autoFocus /></ModalField>
      {err && <div className="text-[11px] text-rose-500 mono">{err}</div>}
      <ModalActions onCancel={onClose} onSave={save} busy={busy} testId="crm-a-save" label="Log" />
    </ModalShell>
  );
}


/* =============================================================
   LEADS · AUTOMATIONS · COPILOT · ADS · ANALYTICS  (mobile-first)
   ============================================================= */

const LEAD_STATUS_COLORS = {
  new:         "#38bdf8",
  lead:        "#38bdf8",
  contacted:   "#a78bfa",
  qualified:   "#22c55e",
  proposal:    "#8b5cf6",
  negotiation: "#ec4899",
  unqualified: "#94a3b8",
  converted:   "#10b981",
  won:         "#10b981",
  lost:        "#fb7185",
};

const LEAD_SOURCE_LABELS = {
  website: "Website", instagram: "Instagram", facebook: "Facebook",
  google_ads: "Google Ads", meta_ads: "Meta Ads", whatsapp: "WhatsApp",
  referral: "Referral", linkedin: "LinkedIn", email: "Email",
  manual: "Manual", other: "Other",
};

function scoreTint(s) {
  const n = Number(s || 0);
  if (n >= 80) return "#ef4444";
  if (n >= 60) return "#f59e0b";
  if (n >= 40) return "#38bdf8";
  return "#94a3b8";
}

/* --------------------------- LEADS --------------------------- */
/* ==============================================================
   LEADS TABLE  (reference-image style)
   - Sort / Filter action bar
   - Rows: checkbox · name · phone · email · city · source chip · product chip
   - Row click → LeadDetailDrawer
   ============================================================== */

// Pastel palette for the "source" chips  ─ mirrors reference images 1 & 2
const SOURCE_CHIP = {
  website:     { bg: "#eef2ff", fg: "#4f46e5", icon: "◉" },
  instagram:   { bg: "#fdf2f8", fg: "#db2777", icon: "▣" },
  facebook:    { bg: "#eff6ff", fg: "#2563eb", icon: "f" },
  google_ads:  { bg: "#fef3c7", fg: "#b45309", icon: "G" },
  meta_ads:    { bg: "#eff6ff", fg: "#2563eb", icon: "M" },
  whatsapp:    { bg: "#ecfdf5", fg: "#059669", icon: "◉" },
  referral:    { bg: "#f5f3ff", fg: "#7c3aed", icon: "◉" },
  linkedin:    { bg: "#eff6ff", fg: "#0369a1", icon: "in" },
  email:       { bg: "#eef2ff", fg: "#4f46e5", icon: "@" },
  manual:      { bg: "#f1f5f9", fg: "#475569", icon: "◉" },
  other:       { bg: "#f1f5f9", fg: "#475569", icon: "◉" },
};

const PRODUCT_CHIP_PALETTE = [
  { bg: "#eef2ff", fg: "#4f46e5" }, // indigo
  { bg: "#fef3c7", fg: "#b45309" }, // amber
  { bg: "#dcfce7", fg: "#15803d" }, // green
  { bg: "#fce7f3", fg: "#be185d" }, // pink
  { bg: "#e0f2fe", fg: "#0369a1" }, // sky
  { bg: "#fef9c3", fg: "#854d0e" }, // yellow
  { bg: "#f3e8ff", fg: "#7e22ce" }, // purple
];

// Potential chips (Low / Medium / High / Not Relevant)
const POTENTIAL_CHIP = {
  low:          { bg: "#f1f5f9", fg: "#475569", label: "Low" },
  medium:       { bg: "#fef3c7", fg: "#b45309", label: "Medium" },
  high:         { bg: "#dcfce7", fg: "#15803d", label: "High" },
  not_relevant: { bg: "#fee2e2", fg: "#b91c1c", label: "Not Relevant" },
};

// Status display labels — pastel chips
const STATUS_CHIP = {
  new:         { bg: "#eef2ff", fg: "#4f46e5", label: "New Lead" },
  lead:        { bg: "#eef2ff", fg: "#4f46e5", label: "Lead" },
  contacted:   { bg: "#e0f2fe", fg: "#0369a1", label: "Contacted" },
  qualified:   { bg: "#dcfce7", fg: "#15803d", label: "Qualified" },
  proposal:    { bg: "#f3e8ff", fg: "#7e22ce", label: "Proposal" },
  negotiation: { bg: "#fce7f3", fg: "#be185d", label: "Negotiation" },
  unqualified: { bg: "#f1f5f9", fg: "#475569", label: "Unqualified" },
  converted:   { bg: "#d1fae5", fg: "#047857", label: "Converted" },
  won:         { bg: "#d1fae5", fg: "#047857", label: "Won" },
  lost:        { bg: "#fee2e2", fg: "#b91c1c", label: "Lost" },
};

const FOLLOWUP_REPEATS = [
  { k: "none",      label: "Do not repeat" },
  { k: "weekly",    label: "Weekly" },
  { k: "monthly",   label: "Monthly" },
  { k: "quarterly", label: "Quarterly" },
  { k: "annually",  label: "Annually" },
];

function toLocalInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}
function fromLocalInput(s) {
  if (!s) return null;
  try { return new Date(s).toISOString(); } catch { return null; }
}
function fmtFollowUp(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
function productChip(label) {
  const s = String(label || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  return PRODUCT_CHIP_PALETTE[s % PRODUCT_CHIP_PALETTE.length];
}

function LeadsView() {
  const [allLeads, setAllLeads] = useState([]);   // full list — for counts
  const [team, setTeam] = useState([]);           // sales/CRM team for assignee dropdown
  const [sourcesList, setSourcesList] = useState([]); // dynamic per-owner list
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [statusTab, setStatusTab] = useState("all");
  const [source, setSource] = useState("");
  const [creating, setCreating] = useState(false);
  const [detail, setDetail] = useState(null);
  const [callFor, setCallFor] = useState(null);
  const [sortDesc, setSortDesc] = useState(true);
  const [showFilter, setShowFilter] = useState(false);
  const ai = useCrmAskAI("leads");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, t, s] = await Promise.all([
        api.get("/crm/leads"),
        api.get("/crm/team"),
        api.get("/crm/sources"),
      ]);
      const data = r.data || [];
      data.sort((a, b) => (sortDesc ? -1 : 1) * String(a.created || "").localeCompare(String(b.created || "")));
      setAllLeads(data);
      setTeam(t.data || []);
      setSourcesList(s.data || []);
    } catch (e) { console.warn(e); }
    finally { setLoading(false); }
  }, [sortDesc]);

  // Optimistic single-lead patch — update local + push server
  const patchLead = async (id, patch) => {
    setAllLeads(cur => cur.map(l => l.id === id ? { ...l, ...patch } : l));
    try { await api.put(`/crm/leads/${id}`, patch); }
    catch (e) { console.warn(e); load(); }
  };

  useEffect(() => { load(); }, [load]);

  // Deal-stage tabs. "Proposal / Negotiation / Won / Lost" are derived from
  // the linked crm_deals collection ideally — for now use the lead.status
  // enum. Values map: Lead → new+lead, Qualified → qualified, others use
  // their own status name if present.
  const STATUS_TABS = [
    { k: "all",         label: "All",         match: () => true },
    { k: "new",         label: "Lead",        match: (l) => (l.status === "new" || l.status === "lead") },
    { k: "qualified",   label: "Qualified",   match: (l) => l.status === "qualified" },
    { k: "proposal",    label: "Proposal",    match: (l) => l.status === "proposal" },
    { k: "negotiation", label: "Negotiation", match: (l) => l.status === "negotiation" },
    { k: "lost",        label: "Lost",        match: (l) => l.status === "lost" },
    { k: "won",         label: "Won",         match: (l) => l.status === "converted" || l.status === "won" },
  ];

  // Compute counts on the FULL list, then filter for the visible tab + q + source
  const counts = useMemo(() => {
    const c = {};
    STATUS_TABS.forEach(t => { c[t.k] = allLeads.filter(t.match).length; });
    return c;
  }, [allLeads]);

  const leads = useMemo(() => {
    const activeMatch = (STATUS_TABS.find(t => t.k === statusTab) || STATUS_TABS[0]).match;
    const qLower = q.trim().toLowerCase();
    return allLeads.filter(l => {
      if (!activeMatch(l)) return false;
      if (source && l.source !== source) return false;
      if (qLower && ![l.name, l.email, l.phone, l.company, l.city, l.product].some(v => String(v || "").toLowerCase().includes(qLower))) return false;
      return true;
    });
  }, [allLeads, statusTab, q, source]);

  return (
    <div className="flex flex-col h-full">
      {/* Top bar */}
      <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 border-b border-slate-100">
        <div>
          <div className="text-[18px] font-semibold text-slate-800">Leads</div>
          <div className="text-[11px] text-slate-500 mt-0.5">{leads.length} shown · {allLeads.length} total</div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="crm-leads-search"
              value={q} onChange={e => setQ(e.target.value)}
              placeholder="Search leads…"
              className="pl-8 pr-3 py-1.5 text-[12px] rounded-md border border-slate-200 bg-white outline-none focus:border-indigo-400 w-40 sm:w-56"
            />
          </div>
          <button
            data-testid="crm-leads-export"
            onClick={async () => {
              try {
                const r = await api.get("/crm/leads/export.csv", { responseType: "blob" });
                const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
                const a = document.createElement("a");
                a.href = url; a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              } catch (e) { console.warn(e); }
            }}
            className="text-[12px] px-3 py-1.5 rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 flex items-center gap-1"
          >
            ↓ Export
          </button>
          <label
            data-testid="crm-leads-import"
            className="text-[12px] px-3 py-1.5 rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 flex items-center gap-1 cursor-pointer"
          >
            ↑ Import
            <input type="file" accept=".csv" className="hidden"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                const fd = new FormData(); fd.append("file", f);
                try {
                  const r = await api.post("/crm/leads/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
                  alert(`Imported: created ${r.data.created}, updated ${r.data.updated}, skipped ${r.data.skipped}`);
                  load();
                } catch (er) { alert("Import failed: " + (er?.response?.data?.detail || er.message)); }
                finally { e.target.value = ""; }
              }} />
          </label>
          <AskAIButton testId="crm-leads-ai" onClick={ai.ask} />
          <button
            data-testid="crm-new-lead"
            onClick={() => setCreating(true)}
            className="text-[12px] font-medium px-3 py-1.5 rounded-md flex items-center gap-1 text-white"
            style={{ background: "#6366f1" }}
          >
            <Plus size={13} /> New Lead
          </button>
        </div>
      </div>

      {/* Status tabs with counts */}
      <div className="flex items-center gap-1 px-4 sm:px-6 py-1 border-b border-slate-100 bg-white overflow-x-auto"
        style={{ scrollbarWidth: "thin" }}>
        {STATUS_TABS.map(t => {
          const active = statusTab === t.k;
          const chip = STATUS_CHIP[t.k === "won" ? "converted" : (t.k === "new" ? "new" : t.k)] || null;
          return (
            <button key={t.k}
              data-testid={`crm-leads-tab-${t.k}`}
              onClick={() => setStatusTab(t.k)}
              className={`whitespace-nowrap flex items-center gap-1.5 px-3 py-2 text-[12px] border-b-2 -mb-[1px] transition ${
                active ? "border-indigo-500 text-indigo-700 font-semibold" : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              {t.label}
              <span className="inline-flex items-center justify-center min-w-[20px] h-[18px] px-1 rounded-full text-[10px] font-semibold"
                style={{
                  background: active ? "#6366f1" : (chip ? chip.bg : "#e2e8f0"),
                  color:      active ? "#ffffff" : (chip ? chip.fg : "#475569"),
                }}>
                {counts[t.k] || 0}
              </span>
            </button>
          );
        })}
      </div>

      {/* Sort / Filter bar (matches reference image 1) */}
      <div className="flex items-center gap-2 px-4 sm:px-6 py-2 border-b border-slate-100 bg-white">
        <button
          data-testid="crm-leads-sort"
          onClick={() => setSortDesc(v => !v)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-200 text-[12px] text-slate-700 hover:bg-slate-50"
        >
          <span style={{ transform: sortDesc ? "" : "scaleY(-1)", display: "inline-block" }}>⇅</span>
          Sort
        </button>
        <button
          data-testid="crm-leads-filter"
          onClick={() => setShowFilter(v => !v)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-[12px] ${showFilter ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}
        >
          ▽ Filter
        </button>
        {showFilter && (
          <div className="flex items-center gap-2">
            <select value={source} onChange={e => setSource(e.target.value)}
              data-testid="crm-leads-filter-source"
              className="text-[12px] px-2 py-1.5 rounded-md border border-slate-200 bg-white">
              <option value="">All Sources</option>
              {sourcesList.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
        )}
        <div className="ml-auto text-[11px] text-slate-400">
          {loading ? "Loading…" : `${leads.length} lead${leads.length === 1 ? "" : "s"}`}
        </div>
      </div>

      {/* Table — horizontal scroll · Lead Name column frozen (sticky left) */}
      <div className="flex-1 overflow-x-auto overflow-y-auto crm-leads-scroll" data-testid="crm-leads-scroll" style={{ maxWidth: "100%" }}>
        <table className="text-[13px]" style={{ borderCollapse: "separate", borderSpacing: 0, minWidth: 2500, width: "max-content" }}>
          <thead>
            <tr className="text-slate-500 bg-slate-50">
              <th className="px-3 py-2.5 text-left font-medium sticky left-0 z-10 bg-slate-50" style={{ minWidth: 90 }}>
                <span className="text-slate-500 text-[11px] uppercase tracking-wider">Lead #</span>
              </th>
              <th className="px-3 py-2.5 text-left font-medium bg-slate-50 border-r border-slate-200"
                  style={{ minWidth: 180 }}>
                <span className="inline-flex items-center gap-1.5 text-slate-500 text-[11px] uppercase tracking-wider">Lead Name</span>
              </th>
              <th className="px-3 py-2.5 text-left font-medium">
                <span className="inline-flex items-center gap-1.5 text-slate-500 text-[11px] uppercase tracking-wider"><Phone size={11}/> Phone</span>
              </th>
              <th className="px-3 py-2.5 text-left font-medium">
                <span className="inline-flex items-center gap-1.5 text-slate-500 text-[11px] uppercase tracking-wider"><MailIcon size={11}/> Email</span>
              </th>
              <th className="px-3 py-2.5 text-left font-medium">
                <span className="inline-flex items-center gap-1.5 text-slate-500 text-[11px] uppercase tracking-wider">📍 City</span>
              </th>
              <th className="px-3 py-2.5 text-left font-medium">
                <span className="text-slate-500 text-[11px] uppercase tracking-wider">Source</span>
              </th>
              <th className="px-3 py-2.5 text-left font-medium">
                <span className="text-slate-500 text-[11px] uppercase tracking-wider">Product</span>
              </th>
              <th className="px-3 py-2.5 text-left font-medium">
                <span className="inline-flex items-center gap-1.5 text-slate-500 text-[11px] uppercase tracking-wider">Status</span>
              </th>
              <th className="px-3 py-2.5 text-left font-medium">
                <span className="inline-flex items-center gap-1.5 text-slate-500 text-[11px] uppercase tracking-wider">Priority</span>
              </th>
              <th className="px-3 py-2.5 text-left font-medium">
                <span className="text-slate-500 text-[11px] uppercase tracking-wider">Score</span>
              </th>
              <th className="px-3 py-2.5 text-left font-medium" style={{ minWidth: 170 }}>
                <span className="text-slate-500 text-[11px] uppercase tracking-wider">Assign</span>
              </th>
              <th className="px-3 py-2.5 text-left font-medium">
                <span className="text-slate-500 text-[11px] uppercase tracking-wider">Follow-Up</span>
              </th>
              <th className="px-3 py-2.5 text-left font-medium" style={{ minWidth: 220 }}>
                <span className="text-slate-500 text-[11px] uppercase tracking-wider">Notes</span>
              </th>
              <th className="px-3 py-2.5 text-left font-medium" style={{ minWidth: 140 }}>
                <span className="text-slate-500 text-[11px] uppercase tracking-wider">Deal Amount</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {leads.map(l => {
              const chip = SOURCE_CHIP[l.source] || SOURCE_CHIP.manual;
              const pchip = productChip(l.product || l.company || "");
              const sChip = STATUS_CHIP[l.status] || STATUS_CHIP.new;
              const potChip = POTENTIAL_CHIP[l.potential] || null;
              const sourceLabel = (sourcesList.find(s => s.key === l.source)?.label)
                || LEAD_SOURCE_LABELS[l.source]
                || (l.source ? String(l.source).replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) : "—");
              return (
                <tr key={l.id}
                    data-testid={`crm-lead-${l.id}`}
                    onClick={() => setDetail(l)}
                    className="border-t border-slate-100 hover:bg-slate-50/70 cursor-pointer">
                  <td className="px-3 py-2.5 text-slate-600 font-mono text-[11.5px] whitespace-nowrap sticky left-0 z-10 bg-white"
                      data-testid={`crm-lead-number-${l.id}`}>
                    {l.lead_number || "—"}
                  </td>
                  <td className="px-3 py-2.5 bg-white border-r border-slate-100" style={{ minWidth: 180 }}>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-800 truncate">{l.name || "(no name)"}</span>
                      {l.do_not_follow && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-md bg-rose-100 text-rose-600 uppercase tracking-wider">DND</span>
                      )}
                      <button onClick={(e) => { e.stopPropagation(); setDetail(l); }}
                        className="text-slate-300 hover:text-slate-600 ml-auto" aria-label="more">
                        ⋯
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-slate-600 font-normal">{l.phone || "—"}</td>
                  <td className="px-3 py-2.5">
                    {l.email
                      ? <span className="text-indigo-600 hover:underline">{l.email}</span>
                      : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-3 py-2.5 text-slate-600">{l.city || "—"}</td>
                  <td className="px-3 py-2.5">
                    <span className="inline-flex items-center gap-1 text-[12px] px-2 py-0.5 rounded-md"
                      style={{ background: chip.bg, color: chip.fg }}>
                      <span className="text-[10px]">{chip.icon}</span>
                      {sourceLabel}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    {(l.product || (l.tags && l.tags[0])) ? (
                      <span className="inline-block text-[12px] px-2 py-0.5 rounded-md"
                        style={{ background: pchip.bg, color: pchip.fg }}>
                        {l.product || l.tags[0]}
                      </span>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                    <select value={l.status || "new"}
                      data-testid={`crm-lead-status-${l.id}`}
                      onChange={(e) => patchLead(l.id, { status: e.target.value })}
                      className="text-[12px] px-2 py-0.5 rounded-md border-0 outline-none appearance-none pr-6 font-medium"
                      style={{
                        background: sChip.bg,
                        color: sChip.fg,
                        backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 20 20' fill='%23${sChip.fg.slice(1)}'><path d='M5 8l5 5 5-5z'/></svg>")`,
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "right 6px center",
                      }}>
                      {Object.entries(STATUS_CHIP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                    <select value={l.potential || ""}
                      data-testid={`crm-lead-priority-${l.id}`}
                      onChange={(e) => patchLead(l.id, { potential: e.target.value || null })}
                      className="text-[12px] px-2 py-0.5 rounded-md border-0 outline-none appearance-none pr-6 font-medium"
                      style={{
                        background: potChip ? potChip.bg : "#f1f5f9",
                        color:      potChip ? potChip.fg : "#94a3b8",
                        backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 20 20' fill='%23${(potChip ? potChip.fg : "94a3b8").replace('#','')}'><path d='M5 8l5 5 5-5z'/></svg>")`,
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "right 6px center",
                      }}>
                      <option value="">—</option>
                      {Object.entries(POTENTIAL_CHIP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2.5">
                    {typeof l.score === "number"
                      ? <span className="inline-block text-[12px] font-bold px-2 py-0.5 rounded-md"
                          style={{ background: `${scoreTint(l.score)}22`, color: scoreTint(l.score) }}>
                          {l.score}
                        </span>
                      : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                    <select value={l.assigned_to || ""}
                      data-testid={`crm-lead-assign-${l.id}`}
                      onChange={(e) => patchLead(l.id, { assigned_to: e.target.value || null })}
                      className="text-[12px] px-2 py-1 rounded-md border border-slate-200 bg-white outline-none focus:border-indigo-400 max-w-[170px]">
                      <option value="">Unassigned</option>
                      {team.map(m => <option key={m.address} value={m.address}>{m.name}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2.5 text-[12px] text-slate-600 whitespace-nowrap">
                    {l.next_follow_up_at ? (
                      <span className="inline-flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 inline-block" />
                        {fmtFollowUp(l.next_follow_up_at)}
                      </span>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                    <InlineNotesCell
                      leadId={l.id}
                      value={l.follow_up_notes || ""}
                      onSave={(v) => patchLead(l.id, { follow_up_notes: v })}
                    />
                  </td>
                  <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                    <InlineDealAmountCell
                      leadId={l.id}
                      value={l.deal_amount}
                      onSave={(v) => patchLead(l.id, { deal_amount: v })}
                    />
                  </td>
                </tr>
              );
            })}
            {!loading && leads.length === 0 && (
              <tr><td colSpan={14} className="text-center py-10 text-slate-400 text-[12px]">
                No leads yet — click <b>+ New Lead</b> or connect Google / Meta Ads.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {ai.open && <AIAskModal title="AI Leads Insight" busy={ai.busy} text={ai.text} onClose={ai.close} />}
      {creating && <NewLeadModal team={team} sourcesList={sourcesList} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
      {detail && (
        <LeadDetailDrawer
          lead={detail}
          team={team}
          onClose={() => setDetail(null)}
          onChanged={load}
          onCall={() => setCallFor(detail)}
        />
      )}
      {callFor && (
        <CallWidget lead={callFor} onClose={() => setCallFor(null)} />
      )}
    </div>
  );
}

function InlineNotesCell({ leadId, value, onSave }) {
  const [v, setV] = useState(value || "");
  const [editing, setEditing] = useState(false);
  useEffect(() => { setV(value || ""); }, [value]);
  const commit = () => {
    setEditing(false);
    if ((v || "").trim() !== (value || "").trim()) onSave((v || "").trim());
  };
  if (editing) {
    return (
      <textarea
        data-testid={`crm-lead-notes-input-${leadId}`}
        autoFocus
        rows={2}
        value={v}
        onChange={(e) => setV(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); e.currentTarget.blur(); } }}
        className="w-full min-w-[200px] text-[12px] px-2 py-1 rounded-md border border-indigo-300 bg-white outline-none focus:border-indigo-500 resize-y"
      />
    );
  }
  return (
    <div
      data-testid={`crm-lead-notes-${leadId}`}
      onClick={() => setEditing(true)}
      className="text-[12px] text-slate-600 min-h-[24px] max-w-[220px] truncate cursor-text hover:bg-slate-50 px-1.5 py-1 rounded"
      title={value || "Click to add notes"}
    >
      {value ? value : <span className="text-slate-300 italic">Click to add…</span>}
    </div>
  );
}

function InlineDealAmountCell({ leadId, value, onSave }) {
  const [v, setV] = useState(value == null ? "" : String(value));
  const [editing, setEditing] = useState(false);
  useEffect(() => { setV(value == null ? "" : String(value)); }, [value]);
  const commit = () => {
    setEditing(false);
    const cleaned = v.trim();
    const num = cleaned === "" ? null : Number(cleaned);
    if (num !== null && Number.isNaN(num)) { setV(value == null ? "" : String(value)); return; }
    if (num !== value) onSave(num);
  };
  if (editing) {
    return (
      <input
        data-testid={`crm-lead-deal-amount-input-${leadId}`}
        type="number"
        step="0.01"
        min="0"
        autoFocus
        value={v}
        onChange={(e) => setV(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }}
        className="w-32 text-[12px] px-2 py-1 rounded-md border border-indigo-300 bg-white outline-none focus:border-indigo-500"
        placeholder="0"
      />
    );
  }
  return (
    <div
      data-testid={`crm-lead-deal-amount-${leadId}`}
      onClick={() => setEditing(true)}
      className="text-[12px] font-medium cursor-text hover:bg-slate-50 px-1.5 py-1 rounded min-h-[24px]"
    >
      {value != null && value !== ""
        ? <span className="text-emerald-700">{fmt(value)}</span>
        : <span className="text-slate-300 italic">— add —</span>}
    </div>
  );
}

function NewLeadModal({ onClose, onCreated, team = [], sourcesList = [], title = "New Lead" }) {
  const [f, setF] = useState({
    name: "", email: "", phone: "", company: "", message: "",
    source: "manual", city: "", product: "", employment: "",
    assigned_to: "", deal_amount: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const save = async () => {
    if (!f.name.trim()) { setErr("Name required"); return; }
    setBusy(true); setErr("");
    try {
      const payload = { ...f, assigned_to: f.assigned_to || null };
      if (payload.deal_amount === "" || payload.deal_amount == null) delete payload.deal_amount;
      else payload.deal_amount = Number(payload.deal_amount);
      await api.post("/crm/leads", payload);
      onCreated && onCreated();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title={title} icon={Sparkles} onClose={onClose}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <ModalField label="Full name"><input data-testid="crm-l-name" className="cinput" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} autoFocus /></ModalField>
        <ModalField label="Company"><input className="cinput" value={f.company} onChange={e => setF({ ...f, company: e.target.value })} /></ModalField>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <ModalField label="Email"><input data-testid="crm-l-email" className="cinput" type="email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} /></ModalField>
        <ModalField label="Phone"><input className="cinput" value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} /></ModalField>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <ModalField label="City"><input className="cinput" value={f.city} onChange={e => setF({ ...f, city: e.target.value })} placeholder="Bengaluru" /></ModalField>
        <ModalField label="Source">
          <select data-testid="crm-l-source" className="cinput" value={f.source} onChange={e => setF({ ...f, source: e.target.value })}>
            {(sourcesList.length ? sourcesList.map(s => ({ key: s.key, label: s.label }))
                                  : Object.entries(LEAD_SOURCE_LABELS).map(([k, v]) => ({ key: k, label: v }))
            ).map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </ModalField>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <ModalField label="Product / Interest"><input className="cinput" value={f.product} onChange={e => setF({ ...f, product: e.target.value })} placeholder="Wealth Management" /></ModalField>
        <ModalField label="Employment"><input className="cinput" value={f.employment} onChange={e => setF({ ...f, employment: e.target.value })} placeholder="Business Owner" /></ModalField>
      </div>
      <ModalField label="Assignee (Team)">
        <select data-testid="crm-l-assignee" className="cinput" value={f.assigned_to} onChange={e => setF({ ...f, assigned_to: e.target.value })}>
          <option value="">Auto-assign (round-robin)</option>
          {team.map(m => <option key={m.address} value={m.address}>{m.name} · {m.designation}</option>)}
        </select>
      </ModalField>
      <ModalField label="Deal Amount (Opportunity)">
        <input data-testid="crm-l-deal-amount" className="cinput" type="number" step="0.01" min="0"
          value={f.deal_amount} onChange={e => setF({ ...f, deal_amount: e.target.value })} placeholder="e.g. 25000" />
      </ModalField>
      <ModalField label="Message / Intent"><textarea className="cinput" rows={3} value={f.message} onChange={e => setF({ ...f, message: e.target.value })} placeholder="What are they looking for?" /></ModalField>
      {err && <div className="text-[11px] text-rose-500 mono">{err}</div>}
      <ModalActions onCancel={onClose} onSave={save} busy={busy} testId="crm-l-save" />
    </ModalShell>
  );
}

/* ==============================================================
   LEAD DETAIL DRAWER  (reference image 2 style — right-side panel)
   ============================================================== */
function LeadDetailDrawer({ lead, onClose, onChanged, onCall, team = [] }) {
  const [l, setL] = useState(lead);
  const [tab, setTab] = useState("profile");
  const [events, setEvents] = useState([]);
  const [evLoading, setEvLoading] = useState(false);
  const { open: openApp } = useWM();
  useEffect(() => setL(lead), [lead]);

  // Opens the Proposal Builder pre-filled from this lead; saving the proposal links it to the lead and moves the lead to the Proposal stage.
  const createProposal = () => {
    const app = APPS.find(a => a.id === "proposal");
    if (app) { openApp(app, { fromLead: { ...l } }); onClose && onClose(); }
  };

  const loadEvents = useCallback(async () => {
    if (!l?.id) return;
    setEvLoading(true);
    try {
      const r = await api.get(`/crm/leads/${l.id}/events`);
      setEvents(r.data || []);
    } catch (e) { console.warn(e); }
    finally { setEvLoading(false); }
  }, [l?.id]);

  useEffect(() => { loadEvents(); }, [loadEvents, l?.updated]);

  const logEvent = async (action, details = {}) => {
    try {
      await api.post(`/crm/leads/${l.id}/events`, { action, details });
      loadEvents();
    } catch (e) { console.warn(e); }
  };

  const initial = (l.name || "?").trim()[0]?.toUpperCase() || "?";
  const pchip = productChip(l.product || l.company || "");

  return (
    <div className="fixed inset-0 z-[500] bg-slate-900/30" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="absolute top-0 right-0 h-full w-full sm:w-[min(520px,94vw)] bg-white border-l border-slate-200 overflow-y-auto"
        data-testid="crm-lead-drawer"
      >
        {/* Header */}
        <div className="p-4 sm:p-5 border-b border-slate-100">
          <div className="flex items-start gap-3">
            <div className="w-14 h-14 rounded-full grid place-items-center text-white text-lg font-semibold shrink-0"
                 style={{ background: `linear-gradient(140deg, ${pchip.fg}, #8b5cf6)` }}>
              {initial}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-800 text-[16px] truncate">{l.name}</div>
              <div className="text-[13px] text-slate-500">{l.phone || "—"}</div>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {l.source && (
                  <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md"
                    style={{ background: SOURCE_CHIP[l.source]?.bg || "#f1f5f9", color: SOURCE_CHIP[l.source]?.fg || "#475569" }}>
                    ⌂ {LEAD_SOURCE_LABELS[l.source]}
                  </span>
                )}
                {l.product && (
                  <span className="text-[11px] px-2 py-0.5 rounded-md"
                    style={{ background: pchip.bg, color: pchip.fg }}>
                    ✦ {l.product}
                  </span>
                )}
              </div>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-800 p-1"><X size={16} /></button>
          </div>

          <button onClick={createProposal} data-testid="crm-lead-create-proposal"
            className="w-full mt-4 flex items-center justify-center gap-1.5 py-2 rounded-md bg-violet-600 text-white text-[13px] font-medium hover:bg-violet-700">
            Create Proposal
          </button>

          {/* Action row */}
          <div className="grid grid-cols-3 gap-2 mt-4">
            <button
              onClick={async () => {
                try {
                  const r = await api.post(`/crm/leads/${l.id}/call`, { note: "" });
                  if (r.data?.queued) {
                    logEvent("called", { phone: l.phone, provider: "dialnexa", call_id: r.data.call_id });
                    alert("Call queued via DialNexa. You'll get a ring on your registered number in a few seconds.");
                  } else {
                    alert(`Call couldn't be queued — ${r.data?.reason || "check DialNexa key in Integrations."}`);
                  }
                } catch (e) {
                  const reason = e?.response?.data?.detail || e.message;
                  if (window.confirm(`${reason}\n\nOpen the phone dialer instead?`)) {
                    if (l.phone) window.open(`tel:${l.phone}`, "_self");
                  }
                }
                onCall && onCall();
              }}
              data-testid="crm-lead-call"
              className="flex items-center justify-center gap-1.5 py-2 rounded-md border border-slate-200 text-[13px] text-slate-700 hover:bg-slate-50">
              <Phone size={14} className="text-emerald-600" /> Call
            </button>
            {l.phone ? (
              <a
                href={`https://wa.me/${normalizeWhatsAppNumber(l.phone)}?text=${encodeURIComponent(`Hi ${l.name}, thanks for reaching out to us.`)}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => logEvent("whatsapp", { phone: l.phone, provider: "wa.me" })}
                data-testid="crm-lead-whatsapp"
                className="flex items-center justify-center gap-1.5 py-2 rounded-md border border-slate-200 text-[13px] text-slate-700 hover:bg-slate-50">
                <span className="text-emerald-500 text-[13px]">◎</span> WhatsApp
              </a>
            ) : (
              <div data-testid="crm-lead-whatsapp" title="No phone number on file"
                className="flex items-center justify-center gap-1.5 py-2 rounded-md border border-slate-200 text-[13px] text-slate-400 cursor-not-allowed">
                <span className="text-slate-300 text-[13px]">◎</span> No phone number
              </div>
            )}
            <button
              onClick={() => {
                if (!l.email) return;
                logEvent("email", { email: l.email });
                window.open(`mailto:${l.email}`, "_blank");
              }}
              data-testid="crm-lead-mail"
              className="flex items-center justify-center gap-1.5 py-2 rounded-md border border-slate-200 text-[13px] text-slate-700 hover:bg-slate-50">
              <MailIcon size={14} className="text-indigo-600" /> Mail
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-slate-100 px-4 sm:px-5">
          {[
            { k: "profile",     label: "Profile",     icon: Users },
            { k: "activity",    label: "Activity",    icon: TrendingUp },
            { k: "followup",    label: "Follow-Up",   icon: RefreshCw },
            { k: "opportunity", label: "Opportunity Details", icon: DollarSign },
          ].map(t => {
            const Ic = t.icon; const active = tab === t.k;
            return (
              <button key={t.k}
                data-testid={`crm-lead-tab-${t.k}`}
                onClick={() => setTab(t.k)}
                className={`flex items-center gap-1.5 px-3 py-2 text-[12px] border-b-2 -mb-[1px] ${active ? "text-indigo-700 border-indigo-500 font-medium" : "text-slate-500 border-transparent hover:text-slate-700"}`}
              >
                <Ic size={13} /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="p-4 sm:p-5">
          {tab === "activity" && (
            <LeadActivityTimeline lead={l} events={events} loading={evLoading} onLog={logEvent} />
          )}
          {tab === "profile" && (
            <div className="space-y-3" data-testid="crm-lead-profile">
              {/* Live status card — assignee, status chip, deal amount, follow-up */}
              <ProfileSummaryCard lead={l} team={team} />

              <div className="text-[11px] text-slate-500">
                Created <span className="text-slate-700 font-medium">{fmtEventTime(l.created)}</span>
                {l.assigned_agent && <> &nbsp;·&nbsp; by <span className="text-indigo-500">{l.assigned_agent}</span></>}
              </div>
              <div className="divide-y divide-slate-100 border border-slate-100 rounded-lg">
                <FieldRow icon={<Users size={13} className="text-slate-400"/>}  label="Name"        value={l.name} />
                <FieldRow icon={<MailIcon size={13} className="text-slate-400"/>} label="Email"     value={l.email || "—"} />
                <FieldRow icon={<Phone size={13} className="text-slate-400"/>}  label="Phone"       value={l.phone || "—"} />
                <FieldRow icon={<Briefcase size={13} className="text-slate-400"/>} label="Employment" value={l.employment || "—"} />
                <FieldRow icon={<Building2 size={13} className="text-slate-400"/>} label="Company"   value={l.company || "—"} />
                <FieldRow icon={<span className="text-slate-400 text-[12px]">📍</span>} label="City" value={l.city || "—"} />
              </div>

              {l.reason && (
                <div className="p-3 rounded-md bg-indigo-50 border border-indigo-100 text-[12px] text-slate-700">
                  <div className="text-[10px] uppercase tracking-widest text-indigo-500 mb-1">AI Reasoning · score {l.score}</div>
                  {l.reason}
                </div>
              )}
            </div>
          )}
          {tab === "followup" && (
            <FollowUpPanel lead={l} onSaved={(next) => { setL(next); onChanged && onChanged(); }} />
          )}
          {tab === "opportunity" && (
            <div className="space-y-3">
              {/* Editable Status + Potential pickers */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Lead Status</div>
                  <select value={l.status || "new"}
                    data-testid="crm-lead-status"
                    onChange={async (e) => {
                      const r = await api.put(`/crm/leads/${l.id}`, { status: e.target.value });
                      setL(r.data); onChanged && onChanged();
                    }}
                    className="w-full text-[13px] px-2 py-1.5 rounded-md border border-slate-200 bg-white outline-none focus:border-indigo-400">
                    {Object.entries(STATUS_CHIP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Priority</div>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(POTENTIAL_CHIP).map(([k, v]) => {
                      const active = l.potential === k;
                      return (
                        <button key={k}
                          data-testid={`crm-lead-potential-${k}`}
                          onClick={async () => {
                            const r = await api.put(`/crm/leads/${l.id}`, { potential: k });
                            setL(r.data); onChanged && onChanged();
                          }}
                          className="text-[12px] px-2 py-1 rounded-md transition"
                          style={{
                            background: active ? v.fg : v.bg,
                            color:      active ? "#fff" : v.fg,
                            border:     active ? `1px solid ${v.fg}` : `1px solid ${v.bg}`,
                          }}>
                          {v.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Assignee</div>
                  <select value={l.assigned_to || ""}
                    data-testid="crm-lead-assignee"
                    onChange={async (e) => {
                      const r = await api.put(`/crm/leads/${l.id}`, { assigned_to: e.target.value || null });
                      setL(r.data); onChanged && onChanged();
                    }}
                    className="w-full text-[13px] px-2 py-1.5 rounded-md border border-slate-200 bg-white outline-none focus:border-indigo-400">
                    <option value="">Unassigned</option>
                    {team.map(m => <option key={m.address} value={m.address}>{m.name} · {m.designation}</option>)}
                  </select>
                </div>
                <div />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <MetaCard label="Product"    value={l.product || "—"} tint={pchip.fg} bg={pchip.bg} />
                <MetaCard label="Employment" value={l.employment || "—"} tint="#059669" bg="#dcfce7" />
                <MetaCard label="Score"      value={l.score ?? "—"} tint={scoreTint(l.score)} />
                <MetaCard label="Follow-Up"  value={fmtFollowUp(l.next_follow_up_at)} tint="#4f46e5" bg="#eef2ff" />
              </div>
              <button
                onClick={async () => {
                  try {
                    const r = await api.post(`/crm/leads/${l.id}/rescore`);
                    setL(r.data); onChanged && onChanged();
                  } catch (e) { console.warn(e); }
                }}
                className="w-full text-[12px] font-medium px-3 py-2 rounded-md border border-indigo-200 text-indigo-700 hover:bg-indigo-50 flex items-center justify-center gap-1.5"
              >
                <RefreshCw size={12} /> AI Rescore
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FieldRow({ icon, label, value }) {
  return (
    <div className="flex items-center gap-3 px-3 py-2">
      {icon}
      <span className="text-[12px] text-slate-500 w-24">{label}</span>
      <span className="text-[13px] text-slate-800 flex-1 truncate">{value}</span>
    </div>
  );
}
function MetaCard({ label, value, tint, bg }) {
  return (
    <div className="p-3 rounded-md border border-slate-100">
      <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">{label}</div>
      <span className="inline-block text-[13px] px-2 py-0.5 rounded font-medium"
        style={{ background: bg || `${tint}15`, color: tint }}>
        {String(value)}
      </span>
    </div>
  );
}

/* ==============================================================
   PROFILE SUMMARY CARD — assignee, status chip, deal amount + follow-up
   ============================================================== */
function ProfileSummaryCard({ lead, team = [] }) {
  const l = lead || {};
  const assignee = team.find(m => m.address === l.assigned_to);
  const assigneeLabel = assignee ? assignee.name : (l.assigned_to || "Unassigned");
  const sChip = STATUS_CHIP[l.status] || STATUS_CHIP.new;
  const followState =
    l.do_not_follow ? { label: "Do Not Follow", tint: "#f43f5e", bg: "#fff1f2" }
    : l.next_follow_up_at ? { label: "Scheduled", tint: "#0ea5e9", bg: "#ecfeff" }
    : { label: "Not scheduled", tint: "#94a3b8", bg: "#f8fafc" };

  return (
    <div className="rounded-lg border border-slate-200 p-3 space-y-2.5" data-testid="crm-lead-profile-summary">
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-[12px]">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">Assignee</div>
          <div className="flex items-center gap-1.5 text-slate-800 font-medium truncate" data-testid="crm-lead-profile-assignee">
            <span className="inline-flex w-5 h-5 rounded-full text-white items-center justify-center text-[10px] font-bold"
              style={{ background: "linear-gradient(135deg,#6366f1,#8b5cf6)" }}>
              {(assigneeLabel || "?").trim()[0].toUpperCase()}
            </span>
            {assigneeLabel}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">Last Status</div>
          <span className="inline-block text-[11.5px] px-2 py-0.5 rounded-md font-medium"
            data-testid="crm-lead-profile-status"
            style={{ background: sChip.bg, color: sChip.fg }}>
            {sChip.label}
          </span>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">Deal Amount</div>
          <div className="text-[13.5px] font-semibold text-emerald-700" data-testid="crm-lead-profile-deal">
            {l.deal_amount != null && l.deal_amount !== "" ? fmt(l.deal_amount) : "—"}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">Follow-Up Status</div>
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="crm-lead-profile-followup-status">
            <span className="inline-block text-[11.5px] px-2 py-0.5 rounded-md font-medium"
              style={{ background: followState.bg, color: followState.tint }}>
              {followState.label}
            </span>
            {l.next_follow_up_at && (
              <span className="text-[11px] text-slate-600 whitespace-nowrap">
                {fmtFollowUp(l.next_follow_up_at)}
              </span>
            )}
            {l.follow_up_repeat && l.follow_up_repeat !== "none" && (
              <span className="text-[9px] uppercase tracking-widest bg-indigo-50 text-indigo-500 px-1.5 py-0.5 rounded">
                {l.follow_up_repeat}
              </span>
            )}
          </div>
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">Follow-Up Note</div>
        <div className="text-[12.5px] text-slate-700 whitespace-pre-wrap"
             data-testid="crm-lead-profile-followup-note">
          {l.follow_up_notes && l.follow_up_notes.trim()
            ? l.follow_up_notes
            : <span className="text-slate-300 italic">No note yet — add one from the Follow-Up tab.</span>}
        </div>
      </div>
    </div>
  );
}


/* ==============================================================
   LEAD ACTIVITY TIMELINE — chronological event log for a single lead
   ============================================================== */

function fmtEventTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return d.toLocaleString(undefined, {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
    });
  } catch { return iso; }
}

const LEAD_EVENT_META = {
  lead_generated:      { label: "Lead generated",       tint: "#10b981", icon: "✚" },
  called:              { label: "Called",               tint: "#059669", icon: "📞" },
  whatsapp:            { label: "WhatsApp",             tint: "#22c55e", icon: "◎" },
  email:               { label: "Email sent",           tint: "#4f46e5", icon: "✉" },
  status_changed:      { label: "Status changed",       tint: "#0284c7", icon: "⇄" },
  potential_changed:   { label: "Priority changed",     tint: "#a855f7", icon: "★" },
  assigned:            { label: "Assigned",             tint: "#6366f1", icon: "◉" },
  followup_scheduled:  { label: "Follow-up scheduled",  tint: "#f59e0b", icon: "⏰" },
  deal_amount_changed: { label: "Deal amount updated",  tint: "#059669", icon: "₹" },
  note:                { label: "Note added",           tint: "#64748b", icon: "✎" },
  custom:              { label: "Activity",             tint: "#64748b", icon: "•" },
};

function eventDescription(e) {
  const d = e.details || {};
  switch (e.action) {
    case "lead_generated":
      return `${d.name ? `${d.name} — ` : ""}via ${LEAD_SOURCE_LABELS[d.source] || d.source || "manual"}`;
    case "called":         return d.phone ? `Dialed ${d.phone}` : "Outbound call";
    case "whatsapp":       return d.phone ? `WhatsApp to ${d.phone}` : "WhatsApp opened";
    case "email":          return d.email ? `Email to ${d.email}` : "Email opened";
    case "status_changed": return `${d.from || "—"} → ${d.to || "—"}`;
    case "potential_changed": return `${d.from || "—"} → ${d.to || "—"}`;
    case "assigned":       return `${d.from || "unassigned"} → ${d.to || "unassigned"}`;
    case "followup_scheduled":
      return `${d.at ? fmtEventTime(d.at) : "scheduled"}${d.notes ? ` · ${d.notes}` : ""}`;
    case "deal_amount_changed":
      return `${d.from ?? 0} → ${d.to ?? 0}`;
    case "note":           return d.text || "";
    default:               return d.text || "";
  }
}

function LeadActivityTimeline({ lead, events, loading, onLog }) {
  const [noteDraft, setNoteDraft] = useState("");
  const submitNote = () => {
    const t = noteDraft.trim();
    if (!t) return;
    onLog("note", { text: t });
    setNoteDraft("");
  };

  const hasGenerated = events.some(e => e.action === "lead_generated");
  const list = hasGenerated ? events : [
    ...events,
    {
      id: "_generated_anchor", action: "lead_generated", ts: lead.created,
      actor: "system",
      details: { name: lead.name, source: lead.source },
    },
  ];

  return (
    <div className="space-y-3" data-testid="crm-lead-activity">
      <div className="flex items-center gap-2">
        <input
          data-testid="crm-lead-activity-note-input"
          value={noteDraft}
          onChange={(e) => setNoteDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") submitNote(); }}
          placeholder="Log a note…"
          className="flex-1 text-[13px] px-2.5 py-1.5 rounded-md border border-slate-200 outline-none focus:border-indigo-400"
        />
        <button
          data-testid="crm-lead-activity-note-save"
          onClick={submitNote}
          disabled={!noteDraft.trim()}
          className="text-[12px] px-3 py-1.5 rounded-md text-white disabled:opacity-40"
          style={{ background: "linear-gradient(120deg,#6366f1,#8b5cf6)" }}>
          Add
        </button>
      </div>

      {loading && <div className="text-[11px] text-slate-400">Loading timeline…</div>}

      <ol className="relative border-l border-slate-200 pl-4 space-y-3">
        {list.map((e, idx) => {
          const meta = LEAD_EVENT_META[e.action] || LEAD_EVENT_META.custom;
          const desc = eventDescription(e);
          return (
            <li key={e.id || idx} data-testid={`crm-lead-event-${e.action}-${idx}`} className="relative">
              <span
                aria-hidden
                className="absolute -left-[22px] top-1 w-4 h-4 rounded-full grid place-items-center text-[9px] text-white font-bold"
                style={{ background: meta.tint }}>
                {meta.icon}
              </span>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-[11px] font-mono text-slate-500">{fmtEventTime(e.ts)}</span>
                <span className="text-[12.5px] font-semibold" style={{ color: meta.tint }}>
                  {meta.label}
                </span>
                {e.actor && e.actor !== "system" && (
                  <span className="text-[10.5px] text-slate-400">by {e.actor}</span>
                )}
              </div>
              {desc && <div className="text-[12px] text-slate-600 mt-0.5">{desc}</div>}
            </li>
          );
        })}
        {!loading && list.length === 0 && (
          <li className="text-[12px] text-slate-400 py-6 text-center">
            No activity yet. Any call, message, follow-up or status change will show up here.
          </li>
        )}
      </ol>
    </div>
  );
}


/* ==============================================================
   FOLLOW-UP PANEL
   - Quick presets: +1h · tomorrow · next week · custom
   - Notes textarea + voice recording (browser MediaRecorder → base64)
   - Repeat cadence · Do-Not-Follow toggle
   ============================================================== */
function FollowUpPanel({ lead, onSaved }) {
  const [when, setWhen] = useState(toLocalInput(lead.next_follow_up_at));
  const [notes, setNotes] = useState(lead.follow_up_notes || "");
  const [repeat, setRepeat] = useState(lead.follow_up_repeat || "none");
  const [dnf, setDnf] = useState(!!lead.do_not_follow);
  const [voice, setVoice] = useState(lead.follow_up_voice_b64 || "");
  const [rec, setRec] = useState(null);            // MediaRecorder
  const [recording, setRecording] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const presets = useMemo(() => {
    const now = new Date();
    const in1h = new Date(now.getTime() + 60 * 60 * 1000);
    const tomorrow = new Date(now); tomorrow.setDate(tomorrow.getDate() + 1);
    const nextWeek = new Date(now); nextWeek.setDate(nextWeek.getDate() + 7);
    return [
      { k: "1h",       label: "In 1 hour",         iso: in1h.toISOString(),      pretty: fmtFollowUp(in1h.toISOString()) },
      { k: "tomorrow", label: "Tomorrow same time",iso: tomorrow.toISOString(),  pretty: fmtFollowUp(tomorrow.toISOString()) },
      { k: "nextweek", label: "Next week same time", iso: nextWeek.toISOString(), pretty: fmtFollowUp(nextWeek.toISOString()) },
    ];
  }, [lead.id]);

  const startRec = async () => {
    setErr("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunks, { type: mr.mimeType || "audio/webm" });
        const fr = new FileReader();
        fr.onloadend = () => setVoice(String(fr.result || ""));
        fr.readAsDataURL(blob);
      };
      mr.start();
      setRec(mr);
      setRecording(true);
    } catch (e) {
      setErr("Mic permission denied — allow microphone access in your browser to record follow-up notes.");
    }
  };
  const stopRec = () => {
    if (rec && rec.state !== "inactive") rec.stop();
    setRecording(false);
  };
  const clearVoice = () => setVoice("");

  const save = async () => {
    setSaving(true); setErr("");
    try {
      const payload = {
        next_follow_up_at: fromLocalInput(when),
        follow_up_notes: notes || null,
        follow_up_voice_b64: voice || null,
        follow_up_repeat: repeat,
        do_not_follow: dnf,
      };
      const r = await api.put(`/crm/leads/${lead.id}`, payload);
      onSaved && onSaved(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="crm-lead-followup-panel">
      {/* Do-Not-Follow */}
      <div className={`flex items-center justify-between p-3 rounded-lg border ${dnf ? "border-rose-200 bg-rose-50" : "border-slate-200 bg-white"}`}>
        <div>
          <div className={`text-[13px] font-medium ${dnf ? "text-rose-700" : "text-slate-800"}`}>Do Not Follow</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Silence all follow-up reminders for this lead.</div>
        </div>
        <button
          data-testid="crm-lead-dnf-toggle"
          onClick={() => setDnf(v => !v)}
          className={`relative w-11 h-6 rounded-full transition ${dnf ? "bg-rose-500" : "bg-slate-300"}`}
        >
          <span className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition"
            style={{ transform: dnf ? "translateX(20px)" : "translateX(0)" }} />
        </button>
      </div>

      {/* Quick presets */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">Next follow-up</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
          {presets.map(p => (
            <button key={p.k}
              data-testid={`crm-lead-follow-preset-${p.k}`}
              onClick={() => setWhen(toLocalInput(p.iso))}
              className="text-[11px] px-2 py-1.5 rounded-md border border-slate-200 hover:bg-indigo-50 hover:border-indigo-300 text-slate-700 text-left disabled:opacity-50"
              disabled={dnf}
            >
              <div className="font-medium">{p.label}</div>
              <div className="text-[10px] text-slate-500 mt-0.5">{p.pretty}</div>
            </button>
          ))}
          <div className="text-[11px] px-2 py-1.5 rounded-md border border-slate-200 text-slate-700 flex flex-col justify-center">
            <div className="font-medium">Custom</div>
            <div className="text-[10px] text-slate-500 mt-0.5">pick date + time</div>
          </div>
        </div>
        <input type="datetime-local"
          data-testid="crm-lead-follow-when"
          value={when}
          disabled={dnf}
          onChange={(e) => setWhen(e.target.value)}
          className="w-full mt-2 text-[13px] px-2 py-1.5 rounded-md border border-slate-200 bg-white outline-none focus:border-indigo-400 disabled:opacity-50" />
      </div>

      {/* Notes */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">Notes</div>
        <textarea
          data-testid="crm-lead-follow-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={dnf}
          rows={3}
          placeholder="What to discuss on the next call…"
          className="w-full text-[13px] px-2 py-1.5 rounded-md border border-slate-200 bg-white outline-none focus:border-indigo-400 disabled:opacity-50"
        />
      </div>

      {/* Voice recording */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">Voice note</div>
        <div className="flex items-center gap-2 flex-wrap">
          {!recording ? (
            <button onClick={startRec} disabled={dnf}
              data-testid="crm-lead-follow-record"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-200 text-[12px] text-slate-700 hover:bg-slate-50 disabled:opacity-50">
              <span className="w-2 h-2 rounded-full bg-rose-500 inline-block" /> Record
            </button>
          ) : (
            <button onClick={stopRec}
              data-testid="crm-lead-follow-stop"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] text-white bg-rose-500 hover:bg-rose-600 animate-pulse">
              <span className="w-2 h-2 rounded-full bg-white inline-block" /> Stop
            </button>
          )}
          {voice && (
            <>
              <audio src={voice} controls className="h-8" data-testid="crm-lead-follow-audio" />
              <button onClick={clearVoice} className="text-slate-400 hover:text-rose-500 text-[11px]" data-testid="crm-lead-follow-audio-clear">
                <Trash2 size={12} />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Repeat */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">Repeat follow-up</div>
        <div className="flex flex-wrap gap-1.5">
          {FOLLOWUP_REPEATS.map(r => (
            <button key={r.k}
              data-testid={`crm-lead-follow-repeat-${r.k}`}
              onClick={() => setRepeat(r.k)}
              disabled={dnf}
              className={`text-[12px] px-2 py-1 rounded-md transition disabled:opacity-50 ${
                repeat === r.k
                  ? "bg-indigo-600 text-white border border-indigo-600"
                  : "bg-white text-slate-700 border border-slate-200 hover:border-indigo-300"
              }`}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {err && <div className="text-[11px] text-rose-500">{err}</div>}

      <button onClick={save} disabled={saving}
        data-testid="crm-lead-follow-save"
        className="w-full text-[13px] font-medium px-3 py-2 rounded-md text-white flex items-center justify-center gap-1.5 disabled:opacity-50"
        style={{ background: "linear-gradient(120deg,#6366f1,#38bdf8)" }}>
        {saving ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
        Save follow-up
      </button>
    </div>
  );
}


/* ==============================================================
   CALL WIDGET  (reference image 3 · UI shell)
   Note: Real telephony requires Twilio Voice / Vonage credentials
   from the user. This UI simulates the connected state so the
   design & UX can be validated ahead of the integration.
   ============================================================== */
function CallWidget({ lead, onClose }) {
  const [seconds, setSeconds] = useState(0);
  const [muted, setMuted] = useState(false);
  const [status, setStatus] = useState("connecting"); // connecting | connected | ended
  const timerRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setStatus("connected"), 1200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (status !== "connected") return;
    timerRef.current = setInterval(() => setSeconds(s => s + 1), 1000);
    return () => clearInterval(timerRef.current);
  }, [status]);

  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");
  const initial = (lead.name || "?").trim()[0]?.toUpperCase() || "?";
  const pchip = productChip(lead.product || "");
  const sTint = LEAD_STATUS_COLORS[lead.status] || "#94a3b8";

  const end = () => { setStatus("ended"); setTimeout(onClose, 400); };

  return (
    <div className="fixed bottom-4 right-4 z-[600] w-[380px] max-w-[94vw] rounded-xl border border-slate-200 bg-white overflow-hidden"
      data-testid="crm-call-widget">
      {/* Status bar */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 bg-slate-50">
        <div className="flex items-center gap-2 text-[12px]">
          <span className="grid place-items-center w-5 h-5 rounded-full text-white text-[11px]"
            style={{ background: status === "connected" ? "#10b981" : status === "ended" ? "#ef4444" : "#f59e0b" }}>
            ✓
          </span>
          <span className="font-semibold text-slate-700 uppercase tracking-wider text-[11px]">
            {status}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setMuted(m => !m)} className={`w-6 h-6 grid place-items-center rounded-full ${muted ? "bg-rose-100 text-rose-600" : "text-slate-500"}`}
            data-testid="crm-call-mute" aria-label="mute">
            {muted ? "🔇" : "🎙"}
          </button>
          <span className="text-[13px] font-mono text-slate-700 tabular-nums">{mm}:{ss}</span>
          <button onClick={end} className="w-6 h-6 rounded-full bg-rose-500 text-white grid place-items-center"
            data-testid="crm-call-end" aria-label="end call">
            <Phone size={12} />
          </button>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-800"><X size={14}/></button>
        </div>
      </div>

      {/* Body */}
      <div className="p-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full grid place-items-center text-white text-[16px] font-semibold"
               style={{ background: `linear-gradient(140deg, ${pchip.fg}, #8b5cf6)` }}>
            {initial}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-slate-800 truncate">
              {lead.name}{lead.product ? ` – ${lead.product}` : ""}
            </div>
            <div className="text-[12px] text-slate-500">{lead.phone || "—"}</div>
          </div>
          <button className="text-slate-400 hover:text-slate-700 text-[13px]" title="open in new tab">↗</button>
        </div>

        <div className="mt-4 divide-y divide-slate-100 border border-slate-100 rounded-md">
          <FieldRow icon={<Users size={13} className="text-slate-400"/>}    label="Name"       value={lead.name} />
          <FieldRow icon={<MailIcon size={13} className="text-slate-400"/>} label="Email"      value={lead.email || "—"} />
          <FieldRow icon={<TrendingUp size={13} className="text-slate-400"/>} label="Lead Stage"
            value={<span className="inline-block px-2 py-0.5 rounded" style={{ background: `${sTint}20`, color: sTint }}>{lead.status || "new"}</span>} />
          <FieldRow icon={<Sparkles size={13} className="text-slate-400"/>}  label="Product"
            value={lead.product
              ? <span className="inline-block px-2 py-0.5 rounded" style={{ background: pchip.bg, color: pchip.fg }}>{lead.product}</span>
              : "—"} />
          <FieldRow icon={<Briefcase size={13} className="text-slate-400"/>} label="Employment"
            value={lead.employment
              ? <span className="inline-block px-2 py-0.5 rounded" style={{ background: "#dcfce7", color: "#059669" }}>{lead.employment}</span>
              : "—"} />
        </div>

        <div className="mt-3 text-[11px] text-slate-400 leading-relaxed">
          <b className="text-slate-500">Note:</b> real voice calling requires Twilio Voice / Vonage credentials. Once connected, recording &amp; AI transcript /
          sentiment scoring will appear here automatically.
        </div>
      </div>
    </div>
  );
}

/* ------------------------- AUTOMATIONS ------------------------- */
const AUTOMATION_TRIGGERS = ["lead_created", "lead_hot", "no_reply_24h", "no_reply_72h", "deal_won", "deal_lost"];
const AUTOMATION_ACTIONS = ["send_email", "send_whatsapp", "create_reminder", "assign_lead"];

function AutomationsView() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/crm/automations");
      setRows(r.data || []);
    } catch (e) { console.warn(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Delete automation?")) return;
    try { await api.delete(`/crm/automations/${id}`); load(); } catch (e) { console.warn(e); }
  };

  return (
    <div className="p-3 sm:p-5 space-y-3 sm:space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <h2 className="text-base sm:text-lg font-semibold flex items-center gap-2">
          <Zap size={16} className="text-[#0ea5e9]" /> Automations
          <span className="mono text-[10px] uppercase tracking-[0.2em] text-slate-400">({rows.length})</span>
        </h2>
        <button
          data-testid="crm-new-automation"
          onClick={() => setCreating(true)}
          className="mono text-[10px] uppercase tracking-[0.24em] px-3 py-1.5 rounded flex items-center gap-1.5 self-start sm:self-auto"
          style={{ background: "linear-gradient(120deg,#0ea5e9,#8b5cf6)", color: "#fff" }}
        >
          <Plus size={11} /> New Rule
        </button>
      </div>

      {loading && <div className="mono text-[11px] uppercase tracking-widest text-slate-400">// loading…</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {rows.map(a => (
          <div key={a.id} data-testid={`crm-automation-${a.id}`}
            className="p-3 rounded-lg border bg-white" style={{ borderColor: "rgba(15,23,42,0.10)" }}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="min-w-0">
                <div className="font-semibold text-sm text-slate-800 truncate">{a.name}</div>
                <div className="flex flex-wrap gap-1 mt-1">
                  <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600">
                    IF {a.trigger}
                  </span>
                  <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600">
                    THEN {a.action}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <span className={`mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded ${a.active ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>
                  {a.active ? "ON" : "OFF"}
                </span>
                <button onClick={() => del(a.id)} className="text-rose-500 hover:text-rose-700 p-1"><Trash2 size={12} /></button>
              </div>
            </div>
            {a.template && (
              <div className="text-[11px] text-slate-500 line-clamp-2 mt-1">{a.template}</div>
            )}
            <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between text-[10px] mono uppercase tracking-widest text-slate-400">
              <span>Fired: {a.fired_count || 0}</span>
              <span>{(a.created || "").slice(0, 10)}</span>
            </div>
          </div>
        ))}
        {!loading && rows.length === 0 && (
          <div className="col-span-full mono text-[11px] uppercase tracking-widest text-slate-400 text-center py-8">
            // no automations yet — set up your first rule
          </div>
        )}
      </div>

      {creating && <NewAutomationModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function NewAutomationModal({ onClose, onCreated }) {
  const [f, setF] = useState({ name: "", trigger: "lead_created", action: "send_email", template: "", active: true });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const save = async () => {
    if (!f.name.trim()) { setErr("Name required"); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/crm/automations", f);
      onCreated && onCreated();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title="New Automation" icon={Zap} onClose={onClose}>
      <ModalField label="Name"><input data-testid="crm-au-name" className="cinput" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="e.g. Welcome hot leads" autoFocus /></ModalField>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <ModalField label="Trigger">
          <select data-testid="crm-au-trigger" className="cinput" value={f.trigger} onChange={e => setF({ ...f, trigger: e.target.value })}>
            {AUTOMATION_TRIGGERS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </ModalField>
        <ModalField label="Action">
          <select data-testid="crm-au-action" className="cinput" value={f.action} onChange={e => setF({ ...f, action: e.target.value })}>
            {AUTOMATION_ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </ModalField>
      </div>
      <ModalField label="Template (subject/body/message)"><textarea className="cinput" rows={4} value={f.template} onChange={e => setF({ ...f, template: e.target.value })} placeholder="Hi {{name}}, thanks for reaching out about {{company}}..." /></ModalField>
      <label className="flex items-center gap-2 text-[12px] text-slate-600">
        <input type="checkbox" checked={f.active} onChange={e => setF({ ...f, active: e.target.checked })} />
        Active — start firing immediately
      </label>
      {err && <div className="text-[11px] text-rose-500 mono">{err}</div>}
      <ModalActions onCancel={onClose} onSave={save} busy={busy} testId="crm-au-save" />
    </ModalShell>
  );
}

/* --------------------------- COPILOT --------------------------- */
function CopilotView() {
  const [messages, setMessages] = useState([
    { role: "assistant", body: "Hey! Ask me anything about your leads, deals, contacts or campaigns. e.g. \"Which hot leads haven't been contacted?\"" }
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId] = useState(() => `copilot-${Date.now()}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async () => {
    const q = input.trim();
    if (!q || busy) return;
    setInput("");
    setMessages(m => [...m, { role: "user", body: q }]);
    setBusy(true);
    try {
      const r = await api.post("/crm/copilot", { question: q, session_id: sessionId });
      setMessages(m => [...m, { role: "assistant", body: r.data.answer || "(no answer)" }]);
    } catch (e) {
      setMessages(m => [...m, { role: "assistant", body: `Error: ${e?.response?.data?.detail || e.message}` }]);
    } finally { setBusy(false); }
  };

  const suggestions = [
    "Top 5 hot leads I should call today",
    "Which deals are stuck in negotiation?",
    "Break down my leads by source",
    "How's my conversion rate this month?",
  ];

  return (
    <div className="flex flex-col h-full">
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 sm:p-5 space-y-3">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[85%] sm:max-w-[70%] px-3 py-2 rounded-lg text-[13px] whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-indigo-600 text-white rounded-br-sm"
                  : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"
              }`}
              data-testid={`crm-copilot-msg-${i}`}
            >
              {m.role === "assistant" && (
                <div className="flex items-center gap-1 mb-1 mono text-[9px] uppercase tracking-widest text-indigo-500">
                  <Bot size={10} /> CO-PILOT
                </div>
              )}
              {m.body}
              {m.role === "assistant" && (
                <button onClick={() => navigator.clipboard.writeText(m.body)}
                  className="mt-1 mono text-[9px] uppercase tracking-widest text-slate-400 hover:text-indigo-600 flex items-center gap-1">
                  <Copy size={9} /> Copy
                </button>
              )}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex items-center gap-2 text-slate-500 mono text-[11px] uppercase tracking-widest">
            <Loader2 size={12} className="animate-spin" /> thinking…
          </div>
        )}
        {messages.length === 1 && (
          <div className="pt-2">
            <div className="mono text-[10px] uppercase tracking-widest text-slate-400 mb-2">Try:</div>
            <div className="flex flex-wrap gap-1.5">
              {suggestions.map((s, i) => (
                <button key={i} onClick={() => setInput(s)}
                  className="text-[11px] px-2 py-1 rounded-full border border-indigo-200 text-indigo-600 bg-white hover:bg-indigo-50">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="p-2 sm:p-3 border-t border-slate-200 bg-white flex items-center gap-2">
        <input
          data-testid="crm-copilot-input"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Ask about your pipeline, leads, deals..."
          className="flex-1 px-3 py-2 text-[13px] rounded-lg border border-slate-200 bg-white outline-none focus:border-indigo-400"
        />
        <button
          data-testid="crm-copilot-send"
          onClick={send} disabled={busy || !input.trim()}
          className="px-3 py-2 rounded-lg text-white disabled:opacity-40 flex items-center gap-1"
          style={{ background: "linear-gradient(120deg,#8b5cf6,#38bdf8)" }}
        >
          <Send size={13} />
        </button>
      </div>
    </div>
  );
}

/* --------------------------- ADS --------------------------- */
function AdsView() {
  const [data, setData] = useState({ credentials: [], webhooks: {} });
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // "google" | "meta"

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/crm/ads/credentials");
      setData(r.data || { credentials: [], webhooks: {} });
    } catch (e) { console.warn(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const google = (data.credentials || []).find(c => c.provider === "google");
  const meta   = (data.credentials || []).find(c => c.provider === "meta");

  return (
    <div className="p-3 sm:p-5 space-y-3 sm:space-y-4">
      <div className="flex items-center gap-2">
        <h2 className="text-base sm:text-lg font-semibold flex items-center gap-2">
          <Megaphone size={16} className="text-[#f97316]" /> Ad Integrations
        </h2>
      </div>
      <div className="text-[12px] text-slate-500 max-w-2xl">
        Connect Google Ads and Meta (Facebook / Instagram) so lead-form submissions land in your CRM automatically.
        AI scoring and round-robin assignment happen the moment a lead arrives.
      </div>

      {loading && <div className="mono text-[11px] uppercase tracking-widest text-slate-400">// loading…</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <AdProviderCard
          provider="google"
          label="Google Ads"
          tint="#fbbf24"
          connected={!!google}
          creds={google}
          webhook={data.webhooks?.google}
          onEdit={() => setEditing("google")}
          onReload={load}
        />
        <AdProviderCard
          provider="meta"
          label="Meta (Facebook / Instagram)"
          tint="#1877f2"
          connected={!!meta}
          creds={meta}
          webhook={data.webhooks?.meta}
          onEdit={() => setEditing("meta")}
          onReload={load}
        />
      </div>

      {editing && (
        <AdCredentialsModal
          provider={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function AdProviderCard({ provider, label, tint, connected, creds, webhook, onEdit }) {
  const [copied, setCopied] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");
  const copy = () => {
    if (!webhook) return;
    navigator.clipboard.writeText(webhook);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };
  const syncNow = async () => {
    setSyncing(true); setSyncMsg("");
    try {
      const { data } = await api.post(`/crm/ads/${provider}/sync`);
      setSyncMsg(`Fetched ${data.fetched} · Added ${data.created} new lead${data.created === 1 ? "" : "s"} · Skipped ${data.skipped} duplicate${data.skipped === 1 ? "" : "s"}`);
    } catch (e) {
      setSyncMsg(e?.response?.data?.detail || "Sync failed.");
    }
    setSyncing(false);
  };
  return (
    <div className="p-4 rounded-lg border bg-white space-y-3" style={{ borderColor: "rgba(15,23,42,0.10)" }}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md grid place-items-center" style={{ background: `${tint}22` }}>
            <Megaphone size={14} style={{ color: tint }} />
          </div>
          <div>
            <div className="font-semibold text-sm text-slate-800">{label}</div>
            <div className="mono text-[9px] uppercase tracking-widest text-slate-400">{provider} ads api</div>
          </div>
        </div>
        <span className={`mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded ${connected ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>
          {connected ? "connected" : "not connected"}
        </span>
      </div>

      {connected && creds && (
        <div className="space-y-1 text-[12px] text-slate-600">
          {provider === "meta" && creds.page_id && <div><span className="mono text-[10px] uppercase tracking-widest text-slate-400">Page:</span> {creds.page_id}</div>}
          {provider !== "meta" && creds.account_id && <div><span className="mono text-[10px] uppercase tracking-widest text-slate-400">Account:</span> {creds.account_id}</div>}
          {creds.client_id && <div className="truncate"><span className="mono text-[10px] uppercase tracking-widest text-slate-400">Client:</span> {creds.client_id}</div>}
          {creds.last_synced && <div><span className="mono text-[10px] uppercase tracking-widest text-slate-400">Last synced:</span> {new Date(creds.last_synced).toLocaleString()}</div>}
        </div>
      )}

      <div>
        <div className="mono text-[9px] uppercase tracking-widest text-slate-400 mb-1">Webhook URL</div>
        <div className="flex items-center gap-1">
          <input readOnly value={webhook || ""}
            data-testid={`crm-ads-webhook-${provider}`}
            className="flex-1 px-2 py-1 text-[11px] rounded border border-slate-200 bg-slate-50 font-mono" />
          <button onClick={copy}
            data-testid={`crm-ads-copy-${provider}`}
            className="px-2 py-1 rounded border border-slate-200 text-slate-500 hover:bg-slate-100 flex items-center gap-1 text-[10px] mono uppercase tracking-widest">
            <Copy size={10} /> {copied ? "copied" : "copy"}
          </button>
        </div>
        <div className="text-[10px] text-slate-400 mt-1">
          Point your {label} lead-form webhook to this URL. Leads will flow in automatically.
        </div>
      </div>

      <div className="flex gap-2">
        <button onClick={onEdit}
          data-testid={`crm-ads-connect-${provider}`}
          className="flex-1 mono text-[10px] uppercase tracking-[0.24em] px-3 py-2 rounded flex items-center justify-center gap-1.5 transition"
          style={{ background: `${tint}18`, color: tint, border: `1px solid ${tint}44` }}
        >
          {connected ? "Update credentials" : "Connect account"}
        </button>
        {provider === "meta" && connected && (
          <button onClick={syncNow}
            disabled={syncing}
            data-testid="crm-ads-sync-meta"
            className="flex-1 mono text-[10px] uppercase tracking-[0.24em] px-3 py-2 rounded flex items-center justify-center gap-1.5 transition bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-60"
          >
            {syncing ? "syncing…" : "sync leads now"}
          </button>
        )}
      </div>
      {syncMsg && <div data-testid="crm-ads-sync-msg" className="text-[10px] text-slate-500 mono">{syncMsg}</div>}
    </div>
  );
}

function AdCredentialsModal({ provider, onClose, onSaved }) {
  const [f, setF] = useState({ provider, client_id: "", client_secret: "", access_token: "", account_id: "", page_id: "", verify_token: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [warn, setWarn] = useState("");
  const save = async () => {
    setBusy(true); setErr(""); setWarn("");
    try {
      const { data } = await api.post("/crm/ads/credentials", f);
      if (data?.subscribe_warning) setWarn(data.subscribe_warning);
      onSaved && onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  const label = provider === "google" ? "Google Ads" : "Meta (Facebook / Instagram)";
  const helpUrl = provider === "google"
    ? "https://developers.google.com/google-ads/api/docs/oauth/overview"
    : "https://developers.facebook.com/docs/marketing-api/system-users/create-retrieve-update";
  return (
    <ModalShell title={`Connect ${label}`} icon={Megaphone} onClose={onClose}>
      <div className="text-[12px] text-slate-500">
        Paste your API credentials below. We store them encrypted — never shown in plaintext again.
        {" "}
        <a href={helpUrl} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">
          Get credentials →
        </a>
      </div>
      {provider === "meta" && (
        <div className="text-[11px] text-slate-400 bg-slate-50 border border-slate-200 rounded p-2">
          Use a Page-scoped access token with <code>leads_retrieval</code> + <code>pages_manage_ads</code> permissions.
          Saving here automatically subscribes the Page to leadgen events. Use "Sync Leads Now" on the card to also
          pull leads directly from the Meta Graph API (in addition to the passive webhook above).
        </div>
      )}
      {provider === "google" ? (
        <ModalField label="Customer / Account ID">
          <input data-testid="crm-ads-account-id" className="cinput" value={f.account_id} onChange={e => setF({ ...f, account_id: e.target.value })} />
        </ModalField>
      ) : (
        <ModalField label="Facebook Page ID">
          <input data-testid="crm-ads-page-id" className="cinput" value={f.page_id} onChange={e => setF({ ...f, page_id: e.target.value })} />
        </ModalField>
      )}
      <ModalField label={provider === "google" ? "OAuth Client ID" : "App ID"}>
        <input data-testid="crm-ads-client-id" className="cinput" value={f.client_id} onChange={e => setF({ ...f, client_id: e.target.value })} />
      </ModalField>
      <ModalField label={provider === "google" ? "OAuth Client Secret" : "App Secret"}>
        <input type="password" data-testid="crm-ads-secret" className="cinput" value={f.client_secret} onChange={e => setF({ ...f, client_secret: e.target.value })} />
      </ModalField>
      <ModalField label={provider === "google" ? "Refresh / Access Token" : "Page Access Token"}>
        <input type="password" data-testid="crm-ads-token" className="cinput" value={f.access_token} onChange={e => setF({ ...f, access_token: e.target.value })} />
      </ModalField>
      {provider === "meta" && (
        <ModalField label="Webhook Verify Token">
          <input data-testid="crm-ads-verify-token" className="cinput" value={f.verify_token} onChange={e => setF({ ...f, verify_token: e.target.value })} placeholder="Pick any string — paste the same one into Meta's webhook setup" />
        </ModalField>
      )}
      {err && <div className="text-[11px] text-rose-500 mono">{err}</div>}
      {warn && <div className="text-[11px] text-amber-600 mono">{warn}</div>}
      <ModalActions onCancel={onClose} onSave={save} busy={busy} testId="crm-ads-save" label="Connect" />
    </ModalShell>
  );
}

/* --------------------------- ANALYTICS --------------------------- */
function AnalyticsView() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/crm/analytics");
        setD(r.data);
      } catch (e) { console.warn(e); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="p-5 mono text-[11px] uppercase tracking-widest text-slate-400">// loading analytics…</div>;
  if (!d)      return <div className="p-5 mono text-[11px] uppercase tracking-widest text-slate-400">// no data</div>;

  return (
    <div className="p-3 sm:p-5 space-y-3 sm:space-y-4">
      <h2 className="text-base sm:text-lg font-semibold flex items-center gap-2">
        <BarChart3 size={16} className="text-[#10b981]" /> Analytics
      </h2>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
        <KPI label="Leads" value={d.leads_total} tint="#38bdf8" />
        <KPI label="Deals" value={d.deals_total} tint="#a78bfa" />
        <KPI label="Won" value={fmt(d.won_value)} tint="#10b981" />
        <KPI label="Pipeline" value={fmt(d.pipeline_value)} tint="#f59e0b" />
        <KPI label="Conversion" value={`${d.conversion_rate}%`} tint="#ec4899" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Section title="Leads by Source" tint="#0ea5e9">
          <BarList data={d.by_source} colorFn={(k) => "#0ea5e9"} />
        </Section>
        <Section title="Leads by Status" tint="#a78bfa">
          <BarList data={d.by_status} colorFn={(k) => LEAD_STATUS_COLORS[k] || "#a78bfa"} />
        </Section>
      </div>

      <Section title="Hot Leads (score ≥ 80)" tint="#ef4444">
        {(d.hot_leads || []).length === 0 ? (
          <div className="mono text-[11px] uppercase tracking-widest text-slate-400 text-center py-4">// no hot leads yet</div>
        ) : (
          <div className="space-y-1.5">
            {d.hot_leads.map(l => (
              <div key={l.id} className="flex items-center justify-between py-1.5 px-2 rounded bg-slate-50 border border-slate-100">
                <div className="min-w-0">
                  <div className="text-[13px] font-medium text-slate-800 truncate">{l.name}</div>
                  <div className="mono text-[9px] uppercase tracking-widest text-slate-500">{LEAD_SOURCE_LABELS[l.source] || l.source}</div>
                </div>
                <div className="mono text-[11px] font-bold px-2 py-0.5 rounded"
                     style={{ background: `${scoreTint(l.score)}22`, color: scoreTint(l.score) }}>
                  {l.score}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function KPI({ label, value, tint }) {
  return (
    <div className="p-2.5 sm:p-3 rounded-lg border bg-white" style={{ borderColor: "rgba(15,23,42,0.10)" }}>
      <div className="mono text-[9px] uppercase tracking-widest text-slate-400">{label}</div>
      <div className="text-lg sm:text-xl font-bold mono truncate" style={{ color: tint }}>{value ?? "—"}</div>
    </div>
  );
}

function Section({ title, tint, children }) {
  return (
    <div className="p-3 sm:p-4 rounded-lg border bg-white" style={{ borderColor: "rgba(15,23,42,0.10)" }}>
      <div className="mono text-[10px] uppercase tracking-[0.28em] mb-2" style={{ color: tint }}>{title}</div>
      {children}
    </div>
  );
}

function BarList({ data, colorFn }) {
  const entries = Object.entries(data || {});
  if (entries.length === 0) return <div className="mono text-[11px] uppercase tracking-widest text-slate-400 text-center py-4">// no data</div>;
  const max = Math.max(...entries.map(([, v]) => Number(v) || 0), 1);
  return (
    <div className="space-y-1.5">
      {entries.map(([k, v]) => {
        const pct = Math.round((Number(v) / max) * 100);
        const c = colorFn ? colorFn(k) : "#a78bfa";
        return (
          <div key={k}>
            <div className="flex items-center justify-between mono text-[10px] uppercase tracking-widest text-slate-500 mb-0.5">
              <span>{LEAD_SOURCE_LABELS[k] || k}</span>
              <span className="text-slate-700 font-semibold">{v}</span>
            </div>
            <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: c }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ==============================================================
   CRM DASHBOARD — Power-BI style executive overview
   Matches user's reference: 8 KPI tiles + Won-deals & Deals-projection
   line charts + Sales-pipeline & Deal-loss donuts + Lead-source
   analysis + Top sales performers + Product-wise top performers.
   ============================================================== */

// palette used across the dashboard — mirrors reference image
const DASH_KPI_TINTS = [
  { grad: "linear-gradient(135deg,#6d28d9,#8b5cf6)", solid: "#7c3aed" }, // Total sales — purple
  { grad: "linear-gradient(135deg,#1e3a8a,#3b82f6)", solid: "#1e40af" }, // Win rate — navy
  { grad: "linear-gradient(135deg,#0369a1,#38bdf8)", solid: "#0284c7" }, // Close rate — blue
  { grad: "linear-gradient(135deg,#047857,#10b981)", solid: "#059669" }, // Avg days — green
];
const DASH_PIE_COLORS = ["#1e40af", "#3b82f6", "#60a5fa", "#93c5fd", "#7c3aed", "#a78bfa", "#f97316", "#f43f5e", "#94a3b8"];

function money(n) {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `${(v / 1_000).toFixed(1)}K`;
  return `${v}`;
}
function monthLabel(m) {
  if (!m) return "";
  const [y, mm] = m.split("-");
  const d = new Date(Number(y), Number(mm) - 1, 1);
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

function CrmDashboard() {
  const [tab, setTab] = useState("sales");
  return (
    <div className="flex flex-col h-full" data-testid="crm-dashboard-tabs">
      <div className="flex items-center gap-1 px-4 sm:px-5 pt-3 border-b border-slate-100 bg-white">
        {[
          { k: "followup", label: "Follow Up", icon: RefreshCw },
          { k: "sales",    label: "Sales",     icon: TrendingUp },
        ].map(t => {
          const Ic = t.icon; const active = tab === t.k;
          return (
            <button key={t.k}
              data-testid={`crm-dashboard-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`flex items-center gap-1.5 px-3 py-2 text-[13px] border-b-2 -mb-[1px] transition ${
                active ? "border-indigo-500 text-indigo-700 font-semibold" : "border-transparent text-slate-500 hover:text-slate-700"
              }`}>
              <Ic size={13} /> {t.label}
            </button>
          );
        })}
      </div>
      <div className="flex-1 overflow-y-auto">
        {tab === "sales"    && <SalesDashboardPane />}
        {tab === "followup" && <FollowUpDashboardPane />}
      </div>
    </div>
  );
}

/* ---------- FOLLOW-UP DASHBOARD PANE ---------- */
function FollowUpDashboardPane() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/crm/leads"); setLeads(r.data || []); }
    catch (e) { console.warn(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const now = Date.now();
  const withFollowUp = leads.filter(l => l.next_follow_up_at && !l.do_not_follow);
  const overdue = withFollowUp.filter(l => new Date(l.next_follow_up_at).getTime() < now)
                              .sort((a, b) => new Date(a.next_follow_up_at) - new Date(b.next_follow_up_at));
  const today   = withFollowUp.filter(l => {
    const t = new Date(l.next_follow_up_at).getTime();
    return t >= now && t < now + 24 * 3600 * 1000;
  }).sort((a, b) => new Date(a.next_follow_up_at) - new Date(b.next_follow_up_at));
  const later   = withFollowUp.filter(l => new Date(l.next_follow_up_at).getTime() >= now + 24 * 3600 * 1000)
                              .sort((a, b) => new Date(a.next_follow_up_at) - new Date(b.next_follow_up_at));
  const dnd = leads.filter(l => l.do_not_follow);

  return (
    <div className="p-4 sm:p-5 space-y-4" data-testid="crm-followup-view">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[18px] font-semibold text-slate-800">Follow-Up Board</div>
          <div className="text-[11px] text-slate-500 mt-0.5">All leads with an upcoming or overdue follow-up · {loading ? "loading…" : `${withFollowUp.length} active`}</div>
        </div>
        <button onClick={load} className="text-[11px] px-3 py-1.5 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50">Refresh</button>
      </div>
      <FollowUpGroup title="Overdue"       list={overdue} tint="#ef4444" />
      <FollowUpGroup title="Today"         list={today}   tint="#f59e0b" />
      <FollowUpGroup title="Upcoming"      list={later}   tint="#3b82f6" />
      {dnd.length > 0 && <FollowUpGroup title="Do Not Follow" list={dnd} tint="#94a3b8" />}
    </div>
  );
}

function FollowUpGroup({ title, list, tint }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={`crm-followup-group-${title.toLowerCase()}`}>
      <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: tint }} />
          <div className="text-[13px] font-semibold text-slate-800">{title}</div>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500">{list.length}</div>
      </div>
      {list.length === 0 ? (
        <div className="text-center py-4 text-[11px] text-slate-400">Nothing here.</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {list.map(l => {
            const sChip = STATUS_CHIP[l.status] || STATUS_CHIP.new;
            const pchip = productChip(l.product || "");
            return (
              <div key={l.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50/60">
                <div className="w-8 h-8 rounded-full grid place-items-center text-white text-[12px] font-semibold shrink-0"
                  style={{ background: `linear-gradient(140deg,${pchip.fg},#8b5cf6)` }}>
                  {(l.name || "?")[0].toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium text-slate-800 truncate">{l.name}</div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {fmtFollowUp(l.next_follow_up_at)}
                    {l.follow_up_notes ? ` · ${l.follow_up_notes.slice(0, 60)}${l.follow_up_notes.length > 60 ? "…" : ""}` : ""}
                  </div>
                </div>
                <span className="text-[11px] px-2 py-0.5 rounded-md shrink-0" style={{ background: sChip.bg, color: sChip.fg }}>
                  {sChip.label}
                </span>
                {l.phone && (
                  <a href={`tel:${l.phone}`} className="text-emerald-600 shrink-0" title="Call">
                    <Phone size={13} />
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ---------- SALES DASHBOARD PANE (was CrmDashboard body) ---------- */
function SalesDashboardPane() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/crm/dashboard");
      setD(r.data);
    } catch (e) { console.warn("[CRM Dashboard]", e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading && !d) return <div className="p-6 text-[12px] text-slate-500">Loading dashboard…</div>;
  if (!d) return <div className="p-6 text-[12px] text-slate-500">Failed to load dashboard.</div>;

  const kpisRow1 = [
    { label: "Total sales",       value: money(d.kpis.total_sales), sub: "" },
    { label: "Win rate",          value: `${d.kpis.win_rate}%` },
    { label: "Close rate",        value: `${d.kpis.close_rate}%` },
    { label: "Avg days to close", value: d.kpis.avg_days_to_close },
  ];
  const kpisRow2 = [
    { label: "Pipeline value",   value: money(d.kpis.pipeline_value) },
    { label: "Open deals",       value: d.kpis.open_deals >= 1000 ? `${(d.kpis.open_deals/1000).toFixed(1)}K` : d.kpis.open_deals },
    { label: "Weighted value",   value: money(d.kpis.weighted_value) },
    { label: "Avg open deal age",value: d.kpis.avg_open_deal_age },
  ];

  const wonSeries  = (d.won_series  || []).map(x => ({ ...x, month: monthLabel(x.month) }));
  const projSeries = (d.proj_series || []).map(x => ({ ...x, month: monthLabel(x.month) }));

  return (
    <div className="p-4 sm:p-5 space-y-4" data-testid="crm-dashboard-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[18px] font-semibold text-slate-800">Sales Dashboard</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Executive overview · live from your pipeline</div>
        </div>
        <button onClick={load} className="text-[11px] px-3 py-1.5 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 flex items-center gap-1"
          data-testid="crm-dashboard-refresh">
          <RefreshCw size={11} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-12 gap-3">
        {/* KPI grid (8 tiles: 4 wide × 2 rows), takes 8 cols on desktop */}
        <div className="col-span-12 lg:col-span-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[...kpisRow1, ...kpisRow2].map((k, i) => {
            const t = DASH_KPI_TINTS[i % 4];
            return (
              <div key={k.label} className="p-4 rounded-xl text-white" style={{ background: t.grad }}
                data-testid={`crm-kpi-${k.label.toLowerCase().replace(/\s+/g,'-')}`}>
                <div className="text-[12px] opacity-90">{k.label}</div>
                <div className="text-[26px] font-semibold mt-1 leading-tight">{k.value}</div>
              </div>
            );
          })}
        </div>

        {/* Sales pipeline donut — right column */}
        <div className="col-span-12 lg:col-span-4 rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-[14px] font-semibold text-slate-800 mb-1">Sales pipeline</div>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={d.pipeline_breakdown} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={2}>
                {(d.pipeline_breakdown || []).map((_, i) => <Cell key={i} fill={DASH_PIE_COLORS[i % DASH_PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v, n) => [v, n]} />
              <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Won deals line chart */}
        <div className="col-span-12 lg:col-span-8 rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-[14px] font-semibold text-slate-800 mb-1">Won deals (last 12 months)</div>
          <ResponsiveContainer width="100%" height={230}>
            <LineChart data={wonSeries} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#64748b" }} />
              <YAxis yAxisId="left"  tick={{ fontSize: 10, fill: "#64748b" }} tickFormatter={money} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: "#64748b" }} />
              <Tooltip formatter={(v, n) => n === "closed_value" ? money(v) : v} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
              <Line yAxisId="left"  type="monotone" dataKey="closed_value" name="Closed value" stroke="#1e40af" strokeWidth={2.5} dot={{ r: 3 }} />
              <Line yAxisId="right" type="monotone" dataKey="won_deals"    name="Won deals"    stroke="#38bdf8" strokeWidth={2.5} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Deal loss donut */}
        <div className="col-span-12 lg:col-span-4 rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-[14px] font-semibold text-slate-800 mb-1">Deal loss reasons</div>
          <ResponsiveContainer width="100%" height={230}>
            <PieChart>
              <Pie data={d.loss_reasons} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2}>
                {(d.loss_reasons || []).map((_, i) => <Cell key={i} fill={DASH_PIE_COLORS[(i + 3) % DASH_PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip />
              <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Deals projection line chart (full width row 2) */}
        <div className="col-span-12 lg:col-span-8 rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-[14px] font-semibold text-slate-800 mb-1">Deals projection (future 12 months)</div>
          <ResponsiveContainer width="100%" height={230}>
            <LineChart data={projSeries} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#64748b" }} />
              <YAxis yAxisId="left"  tick={{ fontSize: 10, fill: "#64748b" }} tickFormatter={money} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: "#64748b" }} />
              <Tooltip formatter={(v, n) => n === "projected_value" ? money(v) : v} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
              <Line yAxisId="left"  type="monotone" dataKey="projected_value" name="Projected value" stroke="#1e40af" strokeWidth={2.5} dot={{ r: 3 }} />
              <Line yAxisId="right" type="monotone" dataKey="deals_due"       name="Deals due"       stroke="#38bdf8" strokeWidth={2.5} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Lead Analysis donut */}
        <div className="col-span-12 lg:col-span-4 rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-[14px] font-semibold text-slate-800 mb-1">Lead analysis</div>
          <div className="text-[11px] text-slate-500 mb-2">by source — organic, ads, referrals & socials</div>
          <ResponsiveContainer width="100%" height={230}>
            <PieChart>
              <Pie data={d.lead_analysis} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2}>
                {(d.lead_analysis || []).map((_, i) => <Cell key={i} fill={DASH_PIE_COLORS[i % DASH_PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip />
              <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Top sales performers */}
        <div className="col-span-12 lg:col-span-8 rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[14px] font-semibold text-slate-800">Top sales performers</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400">Live · sorted by AMT</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px] min-w-[640px]">
              <thead className="text-slate-500">
                <tr className="border-b border-slate-100">
                  <th className="text-left py-2 pl-1 font-medium">Employee</th>
                  <th className="text-left py-2 font-medium">Designation</th>
                  <th className="text-right py-2 font-medium">Leads</th>
                  <th className="text-right py-2 font-medium">Converted</th>
                  <th className="text-right py-2 font-medium">Lost</th>
                  <th className="text-right py-2 font-medium">%</th>
                  <th className="text-right py-2 pr-1 font-medium">AMT</th>
                </tr>
              </thead>
              <tbody>
                {(d.top_performers || []).map((p, i) => (
                  <tr key={p.address} className="border-b border-slate-50 hover:bg-slate-50/60"
                    data-testid={`crm-top-perf-${i}`}>
                    <td className="py-2 pl-1">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full grid place-items-center text-white text-[11px] font-semibold"
                          style={{ background: DASH_PIE_COLORS[i % DASH_PIE_COLORS.length] }}>
                          {(p.name || "?")[0].toUpperCase()}
                        </div>
                        <span className="font-medium text-slate-800">{p.name}</span>
                      </div>
                    </td>
                    <td className="py-2 text-slate-600">{p.designation}</td>
                    <td className="py-2 text-right text-slate-700">{p.leads}</td>
                    <td className="py-2 text-right text-emerald-600 font-medium">{p.converted}</td>
                    <td className="py-2 text-right text-rose-500 font-medium">{p.lost}</td>
                    <td className="py-2 text-right">
                      <span className="inline-block px-1.5 py-0.5 rounded"
                        style={{ background: p.conversion_pct >= 40 ? "#dcfce7" : p.conversion_pct >= 20 ? "#fef3c7" : "#fee2e2",
                                 color:      p.conversion_pct >= 40 ? "#15803d" : p.conversion_pct >= 20 ? "#b45309" : "#b91c1c" }}>
                        {p.conversion_pct}%
                      </span>
                    </td>
                    <td className="py-2 pr-1 text-right font-semibold text-indigo-700">{money(p.amount)}</td>
                  </tr>
                ))}
                {(d.top_performers || []).length === 0 && (
                  <tr><td colSpan={7} className="text-center py-5 text-[11px] text-slate-400">
                    No performers yet — assign leads / deals to team members to populate this.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Product-wise top performers */}
        <div className="col-span-12 lg:col-span-4 rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-[14px] font-semibold text-slate-800 mb-2">Product-wise top performer</div>
          <div className="space-y-3">
            {(d.product_top || []).map((row) => (
              <div key={row.product} className="border border-slate-100 rounded-lg p-2.5"
                data-testid={`crm-product-top-${row.product}`}>
                <div className="text-[11px] text-slate-500 uppercase tracking-widest mb-1.5">{row.product}</div>
                <div className="space-y-1">
                  {row.top.map((b, i) => (
                    <div key={b.address} className="flex items-center justify-between gap-2 text-[12px]">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-5 h-5 grid place-items-center rounded-full text-white text-[10px] font-semibold"
                          style={{ background: i === 0 ? "#f59e0b" : i === 1 ? "#a3a3a3" : "#b45309" }}>
                          {i + 1}
                        </span>
                        <div className="min-w-0">
                          <div className="text-slate-800 truncate">{b.name}</div>
                          <div className="text-[10px] text-slate-500 truncate">{b.designation}</div>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-semibold text-indigo-700">{money(b.amount)}</div>
                        <div className="text-[10px] text-slate-500">{b.converted}/{b.leads} · {b.conversion_pct}%</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {(d.product_top || []).length === 0 && (
              <div className="text-center py-4 text-[11px] text-slate-400">
                Set a <b>Product / Interest</b> on your leads to unlock this ranking.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


/* ==============================================================
   TEAM VIEW  · auto-fetches sales/CRM users + manual add
   ============================================================== */
function TeamView() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [query, setQuery] = useState("");
  const ai = useCrmAskAI("team");

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/crm/team"); setRows(r.data || []); }
    catch (e) { console.warn(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (address) => {
    if (!window.confirm("Remove team member?")) return;
    try { await api.delete(`/crm/team/${encodeURIComponent(address)}`); load(); }
    catch (e) { console.warn(e); }
  };

  const autoCount = rows.filter(r => r.source === "auto").length;
  const tq = query.trim().toLowerCase();
  const shownRows = tq ? rows.filter(m => [m.name, m.address, m.designation, m.phone].some(v => String(v || "").toLowerCase().includes(tq))) : rows;

  return (
    <div className="p-4 sm:p-5 space-y-4" data-testid="crm-team-view">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[18px] font-semibold text-slate-800">Team</div>
          <div className="text-[11px] text-slate-500 mt-0.5">{rows.length} members · {autoCount} auto-fetched from Sales / CRM roles</div>
        </div>
        <div className="flex items-center gap-2">
          <AskAIButton testId="crm-team-ai" onClick={ai.ask} />
          <NewButton testId="crm-team-add" label="New Member" onClick={() => setCreating(true)} />
        </div>
      </div>
      <ToolbarSearch testId="crm-team-search" value={query} onChange={setQuery} placeholder="Search name, email, designation..." />
      {ai.open && <AIAskModal title="AI Team Insight" busy={ai.busy} text={ai.text} onClose={ai.close} />}

      {loading && <div className="text-[11px] text-slate-500">Loading…</div>}

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-[13px]" style={{ minWidth: 1250 }}>
          <thead className="text-slate-500 bg-slate-50">
            <tr>
              <th className="px-3 py-2.5 text-left font-medium text-[11px] uppercase tracking-wider">Name</th>
              <th className="px-3 py-2.5 text-left font-medium text-[11px] uppercase tracking-wider">Designation</th>
              <th className="px-3 py-2.5 text-right font-medium text-[11px] uppercase tracking-wider" style={{ background: "#fef3c7" }}>Leads Worked on</th>
              <th className="px-3 py-2.5 text-right font-medium text-[11px] uppercase tracking-wider" style={{ background: "#fef3c7" }}>Deals Won</th>
              <th className="px-3 py-2.5 text-right font-medium text-[11px] uppercase tracking-wider" style={{ background: "#fef3c7" }}>Deals Open</th>
              <th className="px-3 py-2.5 text-right font-medium text-[11px] uppercase tracking-wider" style={{ background: "#fef3c7" }}>Deals Closed</th>
              <th className="px-3 py-2.5 text-right font-medium text-[11px] uppercase tracking-wider" style={{ background: "#fef3c7" }}>Revenue Assured</th>
              <th className="px-3 py-2.5 text-right font-medium text-[11px] uppercase tracking-wider" style={{ background: "#fef3c7" }}>Revenue in Pipeline</th>
              <th className="px-3 py-2.5 text-right font-medium text-[11px] uppercase tracking-wider" style={{ background: "#fef3c7" }}>Revenue Lost</th>
              <th className="px-3 py-2.5 text-left font-medium text-[11px] uppercase tracking-wider">Contact</th>
              <th className="px-3 py-2.5 text-left font-medium text-[11px] uppercase tracking-wider">Source</th>
              <th className="px-3 py-2.5 text-right font-medium text-[11px] uppercase tracking-wider w-12"></th>
            </tr>
          </thead>
          <tbody>
            {shownRows.map(m => {
              const initial = (m.name || "?")[0].toUpperCase();
              const tint = PRODUCT_CHIP_PALETTE[(initial.charCodeAt(0) || 65) % PRODUCT_CHIP_PALETTE.length];
              const fmtInr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
              return (
                <tr key={m.address} className="border-t border-slate-100 hover:bg-slate-50/60"
                  data-testid={`crm-team-row-${m.address}`}>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <span className="w-7 h-7 rounded-full grid place-items-center text-white text-[12px] font-semibold"
                        style={{ background: tint.fg }}>{initial}</span>
                      <span className="font-medium text-slate-800">{m.name}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-slate-600">{m.designation}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-slate-800">{m.leads_worked ?? 0}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-emerald-600 font-medium">{m.deals_won ?? 0}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-slate-700">{m.deals_open ?? 0}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-slate-700">{m.deals_closed ?? 0}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-emerald-700">{fmtInr(m.revenue_assured)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-indigo-700">{fmtInr(m.revenue_pipeline)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-rose-600">{fmtInr(m.revenue_lost)}</td>
                  <td className="px-3 py-2.5">
                    <div className="text-[12px] text-indigo-600 truncate max-w-[180px]">{m.address}</div>
                    {m.phone && <div className="text-[11px] text-slate-500">{m.phone}</div>}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className="inline-block text-[11px] px-2 py-0.5 rounded-md"
                      style={{
                        background: m.source === "auto" ? "#dcfce7" : "#eef2ff",
                        color:      m.source === "auto" ? "#15803d" : "#4f46e5",
                      }}>
                      {m.source === "auto" ? "Auto" : "Manual"}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    {m.source === "manual" && (
                      <button onClick={() => remove(m.address)}
                        className="text-slate-400 hover:text-rose-500">
                        <Trash2 size={13} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={12} className="text-center py-8 text-[12px] text-slate-400">
                No team yet. Users with role/designation containing <b>&quot;sales&quot;</b> or <b>&quot;crm&quot;</b> will appear here automatically.
              </td></tr>
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="bg-slate-50 border-t-2 border-slate-200 font-semibold">
              <tr>
                <td className="px-3 py-2.5 text-slate-700" colSpan={2}>Total</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{rows.reduce((s, r) => s + (r.leads_worked || 0), 0)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-emerald-700">{rows.reduce((s, r) => s + (r.deals_won || 0), 0)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{rows.reduce((s, r) => s + (r.deals_open || 0), 0)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{rows.reduce((s, r) => s + (r.deals_closed || 0), 0)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-emerald-700">₹{rows.reduce((s, r) => s + (r.revenue_assured || 0), 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-indigo-700">₹{rows.reduce((s, r) => s + (r.revenue_pipeline || 0), 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-rose-600">₹{rows.reduce((s, r) => s + (r.revenue_lost || 0), 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                <td colSpan={3}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {creating && <NewTeamMemberModal onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function NewTeamMemberModal({ onClose, onSaved }) {
  const [f, setF] = useState({ address: "", name: "", designation: "Sales", phone: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const save = async () => {
    if (!f.address.trim() || !/\S+@\S+/.test(f.address)) { setErr("Valid email required"); return; }
    setBusy(true); setErr("");
    try { await api.post("/crm/team", f); onSaved && onSaved(); }
    catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title="Add Team Member" icon={Users} onClose={onClose}>
      <ModalField label="Email"><input data-testid="crm-team-email" className="cinput" type="email" value={f.address} onChange={e => setF({ ...f, address: e.target.value })} autoFocus /></ModalField>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <ModalField label="Name"><input data-testid="crm-team-name" className="cinput" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} /></ModalField>
        <ModalField label="Designation"><input className="cinput" value={f.designation} onChange={e => setF({ ...f, designation: e.target.value })} placeholder="Sales Executive" /></ModalField>
      </div>
      <ModalField label="Phone"><input className="cinput" value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} /></ModalField>
      {err && <div className="text-[11px] text-rose-500">{err}</div>}
      <ModalActions onCancel={onClose} onSave={save} busy={busy} testId="crm-team-save" label="Add" />
    </ModalShell>
  );
}

/* ==============================================================
   PRODUCTS VIEW  · organization catalog
   ============================================================== */
function ProductsView() {
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [analytics, setAnalytics] = useState({ ranked: [], source_breakdown: [], best_combo: null });
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("performance");    // performance | catalog
  const [aiOpen, setAiOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiTop, setAiTop] = useState(null);
  const [aiBestCombo, setAiBestCombo] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, a] = await Promise.all([
        api.get("/crm/products"),
        api.get("/crm/products/analytics"),
      ]);
      setRows(p.data || []);
      setAnalytics(a.data || { ranked: [], source_breakdown: [], best_combo: null });
    } catch (e) { console.warn(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete product?")) return;
    try { await api.delete(`/crm/products/${id}`); load(); } catch (e) { console.warn(e); }
  };

  const askAI = async () => {
    setAiBusy(true); setAiText(""); setAiTop(null); setAiBestCombo(null); setAiOpen(true);
    try {
      const r = await api.post("/crm/products/ai-recommend");
      setAiText(r.data.recommendation || "");
      setAiTop(r.data.top_product || null);
      setAiBestCombo(r.data.best_combo || null);
    } catch (e) {
      setAiText("AI narrative failed: " + (e?.response?.data?.detail || e.message));
    } finally { setAiBusy(false); }
  };

  const ranked = analytics.ranked || [];
  const pq = query.trim().toLowerCase();
  const rankedShown = pq ? ranked.filter(r => String(r.name || "").toLowerCase().includes(pq)) : ranked;
  const rowsShown = pq ? rows.filter(p => [p.name, p.sku, p.vertical].some(v => String(v || "").toLowerCase().includes(pq))) : rows;
  const totals = useMemo(() => {
    const t = { total_leads: 0, deals_won: 0, deals_open: 0, deals_closed: 0,
      revenue_assured: 0, revenue_pipeline: 0, revenue_lost: 0 };
    ranked.forEach(r => {
      t.total_leads += r.total_leads;
      t.deals_won += r.deals_won;
      t.deals_open += r.deals_open;
      t.deals_closed += r.deals_closed;
      t.revenue_assured += r.revenue_assured;
      t.revenue_pipeline += r.revenue_pipeline;
      t.revenue_lost += r.revenue_lost;
    });
    return t;
  }, [ranked]);

  const rankTint = (rank) => rank === 1 ? "#f59e0b" : rank === 2 ? "#94a3b8" : rank === 3 ? "#d97706" : "#cbd5e1";
  const bestCombo = analytics.best_combo;

  return (
    <div className="p-4 sm:p-5 space-y-4" data-testid="crm-products-view">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[18px] font-semibold text-slate-800">Products</div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {ranked.length} products tracked · {rows.length} in catalog
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="crm-products-ai"
            onClick={askAI}
            className="text-[12px] font-medium px-3 py-1.5 rounded-md flex items-center gap-1.5 text-white transition"
            style={{ background: "linear-gradient(120deg,#8b5cf6,#38bdf8)" }}
          >
            <Sparkles size={13} /> Ask AI
          </button>
          <button
            data-testid="crm-product-new"
            onClick={() => setCreating(true)}
            className="text-[12px] font-medium px-3 py-1.5 rounded-md flex items-center gap-1 text-white"
            style={{ background: "#6366f1" }}
          >
            <Plus size={13} /> New Product
          </button>
        </div>
      </div>

      <ToolbarSearch testId="crm-products-search" value={query} onChange={setQuery} placeholder="Search products..." />

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200">
        {[
          { k: "performance", label: "Performance" },
          { k: "catalog",     label: "Catalog"     },
        ].map(t => {
          const active = tab === t.k;
          return (
            <button
              key={t.k}
              data-testid={`crm-products-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`px-3 py-2 text-[12px] border-b-2 -mb-[1px] transition ${active
                ? "border-indigo-500 text-indigo-700 font-semibold"
                : "border-transparent text-slate-500 hover:text-slate-700"}`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "performance" && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
            <KpiTile label="Total Leads"      value={totals.total_leads} tint="#4f46e5" />
            <KpiTile label="Deals Won"        value={totals.deals_won}   tint="#059669" />
            <KpiTile label="Revenue Assured"  value={fmt(totals.revenue_assured)}  tint="#059669" />
            <KpiTile label="Revenue Pipeline" value={fmt(totals.revenue_pipeline)} tint="#0369a1" />
          </div>

          {bestCombo && bestCombo.won > 0 && (
            <div className="p-3.5 rounded-lg border border-emerald-200 bg-gradient-to-r from-emerald-50 to-white flex items-center gap-3"
                 data-testid="crm-products-best-combo">
              <div className="w-10 h-10 rounded-full grid place-items-center text-white text-lg"
                style={{ background: "linear-gradient(140deg,#10b981,#38bdf8)" }}>★</div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-emerald-600 mb-0.5">Best Product × Source Combo</div>
                <div className="text-[14px] font-semibold text-slate-800 truncate">
                  {bestCombo.product} · via {bestCombo.source_label}
                </div>
                <div className="text-[11px] text-slate-500">
                  {bestCombo.won} won of {bestCombo.total} leads · {fmt(bestCombo.revenue_won)} assured
                </div>
              </div>
            </div>
          )}

          <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
            <table className="text-[13px]" style={{ minWidth: 1500, width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
              <thead className="text-slate-500 bg-slate-50">
                <tr>
                  <th className="w-12 px-3 py-2.5 text-left text-[11px] uppercase tracking-wider font-medium">Rank</th>
                  <th className="px-3 py-2.5 text-left text-[11px] uppercase tracking-wider font-medium">Product Name</th>
                  <th className="px-3 py-2.5 text-left text-[11px] uppercase tracking-wider font-medium">Top Source</th>
                  <th className="px-3 py-2.5 text-right text-[11px] uppercase tracking-wider font-medium">Total Leads</th>
                  <th className="px-3 py-2.5 text-right text-[11px] uppercase tracking-wider font-medium">Worked On</th>
                  <th className="px-3 py-2.5 text-right text-[11px] uppercase tracking-wider font-medium">Deals Won</th>
                  <th className="px-3 py-2.5 text-right text-[11px] uppercase tracking-wider font-medium">Deals Open</th>
                  <th className="px-3 py-2.5 text-right text-[11px] uppercase tracking-wider font-medium">Deals Closed</th>
                  <th className="px-3 py-2.5 text-right text-[11px] uppercase tracking-wider font-medium">Rev Assured</th>
                  <th className="px-3 py-2.5 text-right text-[11px] uppercase tracking-wider font-medium">Rev Pipeline</th>
                  <th className="px-3 py-2.5 text-right text-[11px] uppercase tracking-wider font-medium">Rev Lost</th>
                  <th className="px-3 py-2.5 text-right text-[11px] uppercase tracking-wider font-medium">Conv %</th>
                </tr>
              </thead>
              <tbody>
                {rankedShown.map(r => {
                  const pchip = productChip(r.name);
                  const ts = r.top_source;
                  const srcChip = ts ? (SOURCE_CHIP[ts.key] || SOURCE_CHIP.manual) : null;
                  return (
                    <tr key={r.name} data-testid={`crm-product-row-${r.name.replace(/\s+/g, "_")}`}
                        className="border-t border-slate-100 hover:bg-slate-50/60">
                      <td className="px-3 py-2.5">
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full text-[12px] font-bold text-white"
                          style={{ background: rankTint(r.rank) }}>{r.rank}</span>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="inline-flex items-center gap-1.5 text-[12.5px] px-2 py-0.5 rounded-md font-medium"
                          style={{ background: pchip.bg, color: pchip.fg }}>
                          {r.name}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        {ts ? (
                          <span className="inline-flex items-center gap-1 text-[11.5px] px-2 py-0.5 rounded-md"
                            style={{ background: srcChip.bg, color: srcChip.fg }}>
                            <span className="text-[10px]">{srcChip.icon}</span>
                            {ts.label} · {ts.won}w/{ts.total}
                          </span>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-right font-medium text-slate-800">{r.total_leads}</td>
                      <td className="px-3 py-2.5 text-right text-slate-600">{r.leads_worked_on}</td>
                      <td className="px-3 py-2.5 text-right"><span className="font-semibold text-emerald-700">{r.deals_won}</span></td>
                      <td className="px-3 py-2.5 text-right text-sky-700">{r.deals_open}</td>
                      <td className="px-3 py-2.5 text-right text-slate-700">{r.deals_closed}</td>
                      <td className="px-3 py-2.5 text-right text-emerald-700 font-medium">{fmt(r.revenue_assured)}</td>
                      <td className="px-3 py-2.5 text-right text-sky-700">{fmt(r.revenue_pipeline)}</td>
                      <td className="px-3 py-2.5 text-right text-rose-600">{fmt(r.revenue_lost)}</td>
                      <td className="px-3 py-2.5 text-right">
                        <span className="inline-block text-[12px] font-bold px-2 py-0.5 rounded-md"
                          style={{
                            background: r.conversion_pct >= 30 ? "#dcfce7" : r.conversion_pct >= 10 ? "#fef3c7" : "#f1f5f9",
                            color:      r.conversion_pct >= 30 ? "#15803d" : r.conversion_pct >= 10 ? "#b45309" : "#64748b",
                          }}>
                          {r.conversion_pct}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {!loading && ranked.length === 0 && (
                  <tr><td colSpan={12} className="text-center py-8 text-[12px] text-slate-400">
                    No product data yet — tag your leads with a product name in the Leads view.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Product × Source breakdown */}
          {(analytics.source_breakdown || []).length > 0 && (
            <div className="rounded-lg border border-slate-200 bg-white p-3.5" data-testid="crm-products-src-breakdown">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[13px] font-semibold text-slate-800">Product × Lead-Source Matrix</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Top combos by wins</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {analytics.source_breakdown.slice(0, 12).map((c, idx) => {
                  const pchip = productChip(c.product);
                  const srcChip = SOURCE_CHIP[c.source_key] || SOURCE_CHIP.manual;
                  return (
                    <div key={`${c.product}-${c.source_key}-${idx}`}
                         className="p-2.5 rounded-md border border-slate-100 flex flex-col gap-1.5">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[11.5px] px-1.5 py-0.5 rounded" style={{ background: pchip.bg, color: pchip.fg }}>{c.product}</span>
                        <span className="text-slate-300 text-[11px]">×</span>
                        <span className="text-[11.5px] px-1.5 py-0.5 rounded" style={{ background: srcChip.bg, color: srcChip.fg }}>{c.source_label}</span>
                      </div>
                      <div className="flex items-center gap-3 text-[11px] text-slate-600">
                        <span><b className="text-slate-800">{c.total}</b> leads</span>
                        <span><b className="text-emerald-700">{c.won}</b> won</span>
                        {c.revenue_won > 0 && <span className="ml-auto text-emerald-700 font-medium">{fmt(c.revenue_won)}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {tab === "catalog" && (
        <CatalogVerticalTabs
          rows={rowsShown}
          loading={loading}
          onNew={(v) => { setCreating(v || true); }}
          onEdit={setEditing}
          onDelete={remove}
        />
      )}

      {creating && <ProductEditModal
        defaultVertical={typeof creating === "string" ? creating : null}
        onClose={() => setCreating(false)}
        onSaved={() => { setCreating(false); load(); }} />}
      {editing && <ProductEditModal product={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {aiOpen && (
        <AIProductRecommendModal
          busy={aiBusy}
          text={aiText}
          top={aiTop}
          bestCombo={aiBestCombo}
          onClose={() => setAiOpen(false)}
        />
      )}
    </div>
  );
}

function CatalogVerticalTabs({ rows, loading, onNew, onEdit, onDelete }) {
  const [vTab, setVTab] = useState("real_estate");
  const filtered = useMemo(() => rows.filter(p => (p.vertical || "") === vTab), [rows, vTab]);
  const counts = useMemo(() => {
    const m = { all: rows.length };
    Object.keys(PRODUCT_VERTICALS).forEach(k => { m[k] = 0; });
    m.generic = 0;
    rows.forEach(p => {
      const v = p.vertical || "generic";
      m[v] = (m[v] || 0) + 1;
    });
    return m;
  }, [rows]);

  const vertConfig = PRODUCT_VERTICALS[vTab];
  const attrs = vertConfig?.attrs || [];

  return (
    <div className="space-y-3">
      {/* Vertical sub-tab strip */}
      <div className="flex items-center gap-1 flex-wrap">
        {Object.entries(PRODUCT_VERTICALS).map(([k, v]) => {
          const active = vTab === k;
          const c = counts[k] || 0;
          return (
            <button
              key={k}
              data-testid={`crm-catalog-vtab-${k}`}
              onClick={() => setVTab(k)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] transition ${active
                ? "bg-indigo-600 text-white shadow-sm"
                : "bg-white border border-slate-200 text-slate-600 hover:border-indigo-300 hover:text-indigo-600"}`}
            >
              <span>{v.icon}</span>
              <span className="font-medium">{v.label}</span>
              <span className={`text-[10px] ${active ? "text-white/70" : "text-slate-400"} font-mono`}>{c}</span>
            </button>
          );
        })}
        <button
          data-testid={`crm-catalog-vtab-add-${vTab}`}
          onClick={() => onNew(vTab)}
          className="ml-auto text-[11.5px] font-medium px-3 py-1.5 rounded-md flex items-center gap-1 text-indigo-600 border border-dashed border-indigo-300 hover:bg-indigo-50"
        >
          <Plus size={12} /> Add {vertConfig?.label || "Product"}
        </button>
      </div>

      {/* Table for the active vertical */}
      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto"
           data-testid={`crm-catalog-table-${vTab}`}>
        <table className="text-[13px]" style={{ minWidth: 1200, width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
          <thead className="text-slate-500 bg-slate-50">
            <tr>
              <th className="px-3 py-2.5 text-left text-[11px] uppercase tracking-wider font-medium">Name</th>
              {attrs.map(a => (
                <th key={a.key} className="px-3 py-2.5 text-left text-[11px] uppercase tracking-wider font-medium whitespace-nowrap">
                  {a.label}
                </th>
              ))}
              <th className="px-3 py-2.5 text-right text-[11px] uppercase tracking-wider font-medium whitespace-nowrap">
                {vertConfig?.priceLabel || "Price"}
              </th>
              <th className="px-3 py-2.5 text-right text-[11px] uppercase tracking-wider font-medium whitespace-nowrap">
                {vertConfig?.inventoryLabel || "Inventory"}
              </th>
              <th className="px-3 py-2.5 text-center text-[11px] uppercase tracking-wider font-medium">Active</th>
              <th className="w-24 px-3 py-2.5 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(p => {
              const pchip = productChip(p.name);
              const inv = p.inventory;
              const invBadge = inv == null ? { txt: "—", cls: "text-slate-300" }
                : inv <= 0 ? { txt: `${inv} · Out of stock`, cls: "text-rose-600 font-semibold" }
                : inv < 5 ? { txt: `${inv} · Low`, cls: "text-amber-600 font-semibold" }
                : { txt: String(inv), cls: "text-emerald-700 font-medium" };
              return (
                <tr key={p.id} data-testid={`crm-product-row-${p.id}`}
                    className="border-t border-slate-100 hover:bg-slate-50/60">
                  <td className="px-3 py-2.5">
                    <div className="flex flex-col gap-0.5 min-w-[160px]">
                      <span className="inline-flex items-center gap-1.5 text-[12.5px] px-2 py-0.5 rounded-md font-medium w-fit"
                        style={{ background: pchip.bg, color: pchip.fg }}>
                        {p.name}
                      </span>
                      {p.sku && <span className="text-[10px] mono text-slate-400 uppercase">{p.sku}</span>}
                      {p.description && <span className="text-[11px] text-slate-500 line-clamp-1">{p.description}</span>}
                    </div>
                  </td>
                  {attrs.map(a => (
                    <td key={a.key} className="px-3 py-2.5 text-slate-700 whitespace-nowrap">
                      {p.attrs?.[a.key] != null && String(p.attrs[a.key]).trim() !== ""
                        ? <span>{p.attrs[a.key]}</span>
                        : <span className="text-slate-300">—</span>}
                    </td>
                  ))}
                  <td className="px-3 py-2.5 text-right text-indigo-700 font-semibold whitespace-nowrap">
                    {p.currency === "INR" ? "₹" : (p.currency || "")}{Number(p.price || 0).toLocaleString()}
                  </td>
                  <td className={`px-3 py-2.5 text-right whitespace-nowrap ${invBadge.cls}`}>
                    {invBadge.txt}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded ${p.active ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>
                      {p.active ? "ON" : "OFF"}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <div className="flex items-center gap-2 justify-end">
                      <button onClick={() => onEdit(p)} data-testid={`crm-product-edit-${p.id}`}
                        className="text-[11px] text-indigo-600 hover:underline">Edit</button>
                      <button onClick={() => onDelete(p.id)} data-testid={`crm-product-del-${p.id}`}
                        className="text-[11px] text-rose-500 hover:underline">Delete</button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={attrs.length + 5} className="text-center py-10 text-[12px] text-slate-400">
                  No {vertConfig?.label} products yet — click <b>+ Add {vertConfig?.label}</b>.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AIProductRecommendModal({ busy, text, top, bestCombo, onClose }) {
  return (
    <ModalShell title="AI Product Advisor" icon={Sparkles} onClose={onClose}>
      {busy ? (
        <div className="flex items-center gap-2 text-[13px] text-slate-600 py-6 justify-center">
          <Loader2 size={16} className="animate-spin text-indigo-600" />
          Analysing your product performance…
        </div>
      ) : (
        <div className="space-y-3">
          {top && (
            <div className="p-3 rounded-md border border-indigo-100 bg-indigo-50">
              <div className="text-[10px] uppercase tracking-widest text-indigo-500 mb-1">Top-performing product</div>
              <div className="text-[15px] font-semibold text-indigo-900">
                {top.name} · {top.deals_won} won · {top.conversion_pct}%
              </div>
              <div className="text-[11px] text-slate-600 mt-1">
                Rev assured {fmt(top.revenue_assured)} · {top.total_leads} total leads
              </div>
            </div>
          )}
          {bestCombo && bestCombo.won > 0 && (
            <div className="p-3 rounded-md border border-emerald-100 bg-emerald-50">
              <div className="text-[10px] uppercase tracking-widest text-emerald-600 mb-1">Best product × source</div>
              <div className="text-[13.5px] font-semibold text-emerald-900">
                {bestCombo.product} · via {bestCombo.source_label}
              </div>
              <div className="text-[11px] text-slate-600 mt-1">
                {bestCombo.won} won of {bestCombo.total} · {fmt(bestCombo.revenue_won)} assured
              </div>
            </div>
          )}
          <div className="p-3 rounded-md border border-slate-200 bg-white text-[13px] text-slate-700 whitespace-pre-wrap leading-relaxed"
               data-testid="crm-product-ai-text">
            {text}
          </div>
        </div>
      )}
      <div className="flex items-center justify-end pt-1">
        <button data-testid="crm-product-ai-close" onClick={onClose}
          className="mono text-[10px] uppercase tracking-[0.24em] text-slate-500 hover:text-slate-800 px-3 py-1.5">Close</button>
      </div>
    </ModalShell>
  );
}

/* ==============================================================
   LEAD SOURCES VIEW — per-source analytics + custom source manager
   ============================================================== */
function LeadSourcesView() {
  const [sources, setSources] = useState([]);
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiTop, setAiTop] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([
        api.get("/crm/sources"),
        api.get("/crm/sources/analytics"),
      ]);
      setSources(s.data || []);
      setRows(a.data || []);
    } catch (e) { console.warn(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (sid) => {
    if (!window.confirm("Remove this source from your list? Existing leads with this source will remain.")) return;
    try { await api.delete(`/crm/sources/${sid}`); load(); } catch (e) { console.warn(e); }
  };

  const askAI = async () => {
    setAiBusy(true); setAiText(""); setAiTop(null); setAiOpen(true);
    try {
      const r = await api.post("/crm/sources/ai-recommend");
      setAiText(r.data.recommendation || "");
      setAiTop(r.data.top_source || null);
    } catch (e) {
      setAiText("AI narrative failed: " + (e?.response?.data?.detail || e.message));
    } finally { setAiBusy(false); }
  };

  const totals = useMemo(() => {
    const t = { total_leads: 0, deals_won: 0, deals_open: 0, deals_closed: 0,
      revenue_assured: 0, revenue_pipeline: 0, revenue_lost: 0 };
    rows.forEach(r => {
      t.total_leads += r.total_leads;
      t.deals_won += r.deals_won;
      t.deals_open += r.deals_open;
      t.deals_closed += r.deals_closed;
      t.revenue_assured += r.revenue_assured;
      t.revenue_pipeline += r.revenue_pipeline;
      t.revenue_lost += r.revenue_lost;
    });
    return t;
  }, [rows]);

  const sq = query.trim().toLowerCase();
  const shownRows = sq ? rows.filter(r => [r.label, r.key].some(v => String(v || "").toLowerCase().includes(sq))) : rows;
  const idByKey = useMemo(() => Object.fromEntries(sources.map(s => [s.key, s.id])), [sources]);
  const rankTint = (rank) => rank === 1 ? "#f59e0b" : rank === 2 ? "#94a3b8" : rank === 3 ? "#d97706" : "#cbd5e1";

  return (
    <div className="p-4 sm:p-5 space-y-4" data-testid="crm-sources-view">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[18px] font-semibold text-slate-800">Lead Sources</div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {rows.length} sources · ranked by conversion % · click <b>Ask AI</b> for a narrative recommendation
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="crm-sources-ai"
            onClick={askAI}
            className="text-[12px] font-medium px-3 py-1.5 rounded-md flex items-center gap-1.5 text-white transition"
            style={{ background: "linear-gradient(120deg,#8b5cf6,#38bdf8)" }}
          >
            <Sparkles size={13} /> Ask AI
          </button>
          <button
            data-testid="crm-sources-new"
            onClick={() => setCreating(true)}
            className="text-[12px] font-medium px-3 py-1.5 rounded-md flex items-center gap-1 text-white"
            style={{ background: "#6366f1" }}
          >
            <Plus size={13} /> New Source
          </button>
        </div>
      </div>

      <ToolbarSearch testId="crm-sources-search" value={query} onChange={setQuery} placeholder="Search sources..." />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        <KpiTile label="Total Leads"       value={totals.total_leads} tint="#4f46e5" />
        <KpiTile label="Deals Won"         value={totals.deals_won}   tint="#059669" />
        <KpiTile label="Revenue Assured"   value={fmt(totals.revenue_assured)}  tint="#059669" />
        <KpiTile label="Revenue Pipeline"  value={fmt(totals.revenue_pipeline)} tint="#0369a1" />
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="text-[13px]" style={{ minWidth: 1400, width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
          <thead className="text-slate-500 bg-slate-50">
            <tr>
              <th className="w-12 px-3 py-2.5 text-left text-[11px] uppercase tracking-wider font-medium">Rank</th>
              <th className="px-3 py-2.5 text-left text-[11px] uppercase tracking-wider font-medium">Source</th>
              <th className="px-3 py-2.5 text-right text-[11px] uppercase tracking-wider font-medium">Total Leads</th>
              <th className="px-3 py-2.5 text-right text-[11px] uppercase tracking-wider font-medium">Worked On</th>
              <th className="px-3 py-2.5 text-right text-[11px] uppercase tracking-wider font-medium">Deals Won</th>
              <th className="px-3 py-2.5 text-right text-[11px] uppercase tracking-wider font-medium">Deals Open</th>
              <th className="px-3 py-2.5 text-right text-[11px] uppercase tracking-wider font-medium">Deals Closed</th>
              <th className="px-3 py-2.5 text-right text-[11px] uppercase tracking-wider font-medium">Rev Assured</th>
              <th className="px-3 py-2.5 text-right text-[11px] uppercase tracking-wider font-medium">Rev Pipeline</th>
              <th className="px-3 py-2.5 text-right text-[11px] uppercase tracking-wider font-medium">Rev Lost</th>
              <th className="px-3 py-2.5 text-right text-[11px] uppercase tracking-wider font-medium">Conv %</th>
              <th className="w-16 px-3 py-2.5 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {shownRows.map(r => {
              const chip = SOURCE_CHIP[r.key] || SOURCE_CHIP.manual;
              const sid = idByKey[r.key];
              const rankColor = rankTint(r.rank);
              return (
                <tr key={r.key} data-testid={`crm-source-row-${r.key}`}
                    className="border-t border-slate-100 hover:bg-slate-50/60">
                  <td className="px-3 py-2.5">
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full text-[12px] font-bold text-white"
                      style={{ background: rankColor }}>
                      {r.rank}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className="inline-flex items-center gap-1.5 text-[12.5px] px-2 py-0.5 rounded-md font-medium"
                      style={{ background: chip.bg, color: chip.fg }}>
                      <span className="text-[10px]">{chip.icon}</span>
                      {r.label}
                      {r.custom && (
                        <span className="ml-1 text-[9px] uppercase tracking-widest bg-white/60 px-1 rounded">custom</span>
                      )}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right font-medium text-slate-800">{r.total_leads}</td>
                  <td className="px-3 py-2.5 text-right text-slate-600">{r.leads_worked_on}</td>
                  <td className="px-3 py-2.5 text-right"><span className="font-semibold text-emerald-700">{r.deals_won}</span></td>
                  <td className="px-3 py-2.5 text-right text-sky-700">{r.deals_open}</td>
                  <td className="px-3 py-2.5 text-right text-slate-700">{r.deals_closed}</td>
                  <td className="px-3 py-2.5 text-right text-emerald-700 font-medium">{fmt(r.revenue_assured)}</td>
                  <td className="px-3 py-2.5 text-right text-sky-700">{fmt(r.revenue_pipeline)}</td>
                  <td className="px-3 py-2.5 text-right text-rose-600">{fmt(r.revenue_lost)}</td>
                  <td className="px-3 py-2.5 text-right">
                    <span className="inline-block text-[12px] font-bold px-2 py-0.5 rounded-md"
                      style={{
                        background: r.conversion_pct >= 30 ? "#dcfce7" : r.conversion_pct >= 10 ? "#fef3c7" : "#f1f5f9",
                        color:      r.conversion_pct >= 30 ? "#15803d" : r.conversion_pct >= 10 ? "#b45309" : "#64748b",
                      }}>
                      {r.conversion_pct}%
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    {sid && (
                      <button
                        data-testid={`crm-source-del-${r.key}`}
                        onClick={() => remove(sid)}
                        title="Hide this source"
                        className="text-slate-300 hover:text-rose-500 p-1">
                        <Trash2 size={13} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={12} className="text-center py-8 text-[12px] text-slate-400">
                No lead sources yet — click <b>+ Add Source</b>.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {creating && (
        <NewSourceModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />
      )}
      {aiOpen && (
        <AISourceRecommendModal
          busy={aiBusy}
          text={aiText}
          top={aiTop}
          onClose={() => setAiOpen(false)}
        />
      )}
    </div>
  );
}

function KpiTile({ label, value, tint }) {
  return (
    <div className="p-3 rounded-lg border border-slate-200 bg-white">
      <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">{label}</div>
      <div className="text-[16px] font-semibold" style={{ color: tint }}>{value}</div>
    </div>
  );
}

function NewSourceModal({ onClose, onCreated }) {
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const save = async () => {
    if (!label.trim()) { setErr("Label required"); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/crm/sources", { label: label.trim() });
      onCreated && onCreated();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title="New Lead Source" icon={Megaphone} onClose={onClose}>
      <ModalField label="Source name">
        <input data-testid="crm-source-label" className="cinput" value={label}
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") save(); }}
          placeholder="e.g. YouTube Ads, Trade Show, Podcast Sponsorship" autoFocus />
      </ModalField>
      <div className="text-[11px] text-slate-500">
        New sources become available in every lead&apos;s source picker.
      </div>
      {err && <div className="text-[11px] text-rose-500 mono">{err}</div>}
      <ModalActions onCancel={onClose} onSave={save} busy={busy} testId="crm-source-save" />
    </ModalShell>
  );
}

function AISourceRecommendModal({ busy, text, top, onClose }) {
  return (
    <ModalShell title="AI Lead Source Advisor" icon={Sparkles} onClose={onClose}>
      {busy ? (
        <div className="flex items-center gap-2 text-[13px] text-slate-600 py-6 justify-center">
          <Loader2 size={16} className="animate-spin text-indigo-600" />
          Analysing your funnel…
        </div>
      ) : (
        <div className="space-y-3">
          {top && (
            <div className="p-3 rounded-md border border-indigo-100 bg-indigo-50">
              <div className="text-[10px] uppercase tracking-widest text-indigo-500 mb-1">Top-performing source</div>
              <div className="text-[15px] font-semibold text-indigo-900">
                {top.label} · {top.conversion_pct}% conversion
              </div>
              <div className="text-[11px] text-slate-600 mt-1">
                {top.deals_won} won of {top.total_leads} leads · Rev assured {fmt(top.revenue_assured)}
              </div>
            </div>
          )}
          <div className="p-3 rounded-md border border-slate-200 bg-white text-[13px] text-slate-700 whitespace-pre-wrap leading-relaxed"
               data-testid="crm-source-ai-text">
            {text}
          </div>
        </div>
      )}
      <div className="flex items-center justify-end pt-1">
        <button data-testid="crm-source-ai-close" onClick={onClose}
          className="mono text-[10px] uppercase tracking-[0.24em] text-slate-500 hover:text-slate-800 px-3 py-1.5">Close</button>
      </div>
    </ModalShell>
  );
}

// Product verticals — each vertical exposes its own set of attribute fields
// that become columns in the catalog table and inputs in the New Product modal.
const PRODUCT_VERTICALS = {
  real_estate: {
    label: "Real Estate",
    icon: "🏠",
    priceLabel: "Price",
    inventoryLabel: "Units Available",
    attrs: [
      { key: "property_type", label: "Type",     type: "select", options: ["Residential", "Commercial", "Plot", "Villa", "Apartment"] },
      { key: "location",      label: "Location", type: "text",   placeholder: "e.g. Mumbai · Bandra West" },
      { key: "area_sqft",     label: "Area (sqft)", type: "number" },
      { key: "bedrooms",      label: "Bedrooms", type: "number" },
      { key: "bathrooms",     label: "Bathrooms", type: "number" },
      { key: "status",        label: "Status",   type: "select", options: ["Available", "Reserved", "Sold"], default: "Available" },
    ],
  },
  it: {
    label: "IT Company",
    icon: "💻",
    priceLabel: "Price / License",
    inventoryLabel: "Licenses Available",
    attrs: [
      { key: "product_category", label: "Category",    type: "select", options: ["SaaS", "Service", "Hardware", "Consulting", "Support"] },
      { key: "license_type",     label: "License Type", type: "select", options: ["Perpetual", "Subscription", "Named-user", "Concurrent", "Site-wide"] },
      { key: "version",          label: "Version",      type: "text", placeholder: "v1.0.0" },
      { key: "support_tier",     label: "Support",      type: "select", options: ["Basic", "Standard", "Premium", "Enterprise"] },
      { key: "delivery",         label: "Delivery",     type: "select", options: ["Cloud", "On-Premise", "Hybrid"] },
    ],
  },
  automotive: {
    label: "Automotive",
    icon: "🚗",
    priceLabel: "Ex-Showroom Price",
    inventoryLabel: "Units In Stock",
    attrs: [
      { key: "vehicle_type", label: "Type",   type: "select", options: ["Car", "SUV", "Bike", "Scooter", "Truck", "EV", "Commercial"] },
      { key: "brand",        label: "Brand",  type: "text", placeholder: "e.g. Toyota, Tata" },
      { key: "model_year",   label: "Model Year", type: "number", placeholder: "2026" },
      { key: "fuel_type",    label: "Fuel Type",  type: "select", options: ["Petrol", "Diesel", "EV", "Hybrid", "CNG"] },
      { key: "transmission", label: "Transmission", type: "select", options: ["Manual", "Automatic", "AMT", "CVT"] },
      { key: "color",        label: "Colour", type: "text" },
    ],
  },
  education: {
    label: "Education",
    icon: "🎓",
    priceLabel: "Fee",
    inventoryLabel: "Seats Available",
    attrs: [
      { key: "program_level", label: "Level",    type: "select", options: ["School", "College", "Certification", "PG", "Diploma", "Workshop"] },
      { key: "duration",      label: "Duration", type: "text", placeholder: "e.g. 6 months" },
      { key: "mode",          label: "Mode",     type: "select", options: ["Online", "Offline", "Hybrid"] },
      { key: "batch_size",    label: "Batch Size", type: "number" },
      { key: "start_date",    label: "Start Date", type: "date" },
      { key: "instructor",    label: "Instructor / Institute", type: "text" },
    ],
  },
};

function ProductEditModal({ product, onClose, onSaved, defaultVertical = null }) {
  const [f, setF] = useState(() => ({
    name: product?.name || "",
    description: product?.description || "",
    price: product?.price ?? 0,
    currency: product?.currency || "INR",
    sku: product?.sku || "",
    category: product?.category || "",
    active: product?.active ?? true,
    vertical: product?.vertical || defaultVertical || "",
    inventory: product?.inventory ?? "",
    attrs: product?.attrs || {},
  }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const vertConfig = f.vertical ? PRODUCT_VERTICALS[f.vertical] : null;

  const setAttr = (k, v) => setF(s => ({ ...s, attrs: { ...s.attrs, [k]: v } }));

  const save = async () => {
    if (!f.name.trim()) { setErr("Product name required"); return; }
    setBusy(true); setErr("");
    try {
      const payload = {
        ...f,
        price: Number(f.price || 0),
        inventory: f.inventory === "" || f.inventory == null ? null : Number(f.inventory),
        vertical: f.vertical || null,
      };
      if (product) await api.put(`/crm/products/${product.id}`, payload);
      else         await api.post("/crm/products", payload);
      onSaved && onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title={product ? "Edit Product" : "New Product"} icon={Building2} onClose={onClose}>
      <ModalField label="Vertical">
        <select data-testid="crm-product-vertical" className="cinput" value={f.vertical}
          onChange={e => setF({ ...f, vertical: e.target.value, attrs: {} })}>
          <option value="">— Generic —</option>
          {Object.entries(PRODUCT_VERTICALS).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
        </select>
      </ModalField>
      <ModalField label={vertConfig ? `${vertConfig.label} Name` : "Name"}>
        <input data-testid="crm-product-name" className="cinput" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} autoFocus
          placeholder={vertConfig ? {
            real_estate: "e.g. Skyline Villa 3BHK",
            it: "e.g. Hexa CRM SaaS",
            automotive: "e.g. Tata Nexon EV Max",
            education: "e.g. Data Science Bootcamp",
          }[f.vertical] : "Product name"} />
      </ModalField>
      <ModalField label="Description"><textarea className="cinput" rows={2} value={f.description} onChange={e => setF({ ...f, description: e.target.value })} /></ModalField>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <ModalField label={vertConfig ? vertConfig.priceLabel : "Price"}>
          <input type="number" data-testid="crm-product-price" className="cinput" value={f.price} onChange={e => setF({ ...f, price: e.target.value })} />
        </ModalField>
        <ModalField label="Currency">
          <select className="cinput" value={f.currency} onChange={e => setF({ ...f, currency: e.target.value })}>
            {["INR","USD","EUR","GBP","AED"].map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </ModalField>
        <ModalField label={vertConfig ? vertConfig.inventoryLabel : "Inventory"}>
          <input type="number" data-testid="crm-product-inventory" className="cinput" min="0" value={f.inventory}
            onChange={e => setF({ ...f, inventory: e.target.value })} placeholder="e.g. 10" />
        </ModalField>
      </div>
      {vertConfig && (
        <div className="p-3 rounded-md border border-slate-200 bg-slate-50/60 space-y-3">
          <div className="text-[11px] uppercase tracking-widest text-slate-500">
            {vertConfig.icon} {vertConfig.label} Attributes
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {vertConfig.attrs.map(a => (
              <ModalField key={a.key} label={a.label}>
                {a.type === "select" ? (
                  <select data-testid={`crm-product-attr-${a.key}`} className="cinput"
                    value={f.attrs[a.key] ?? a.default ?? ""}
                    onChange={e => setAttr(a.key, e.target.value)}>
                    <option value="">—</option>
                    {a.options.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input data-testid={`crm-product-attr-${a.key}`} className="cinput"
                    type={a.type} min={a.type === "number" ? 0 : undefined}
                    value={f.attrs[a.key] ?? ""}
                    onChange={e => setAttr(a.key, e.target.value)}
                    placeholder={a.placeholder || ""} />
                )}
              </ModalField>
            ))}
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <ModalField label="SKU / Code"><input className="cinput" value={f.sku} onChange={e => setF({ ...f, sku: e.target.value })} placeholder="WM-001" /></ModalField>
        <ModalField label="Category tag"><input className="cinput" value={f.category} onChange={e => setF({ ...f, category: e.target.value })} placeholder="Wealth Management" /></ModalField>
      </div>
      <label className="flex items-center gap-2 text-[12px] text-slate-600">
        <input type="checkbox" checked={f.active} onChange={e => setF({ ...f, active: e.target.checked })} />
        Active — visible when creating deals & quotes
      </label>
      {err && <div className="text-[11px] text-rose-500">{err}</div>}
      <ModalActions onCancel={onClose} onSave={save} busy={busy} testId="crm-product-save" label={product ? "Save" : "Create"} />
    </ModalShell>
  );
}

